/**
 * The record page for tasks, punch items and observations — Claude Design
 * "Led AI - Record Detail Web v2".
 *
 * One reading column (title, description, photos, activity) beside a 280px
 * properties sidebar. Every property is its own control: click the value and
 * a popover opens under it — a flat list, a tree, a people directory or a
 * month grid — with search where the list is long, and the arrow keys,
 * Home/End, Enter and Escape all working. A pick saves at once.
 *
 * ## The data and the writes are the console's existing ones
 *
 * Values, directories and the patch come from `usePropertyState` — the same
 * state `PropertyEditor` and the Review queue use — so this page cannot
 * disagree with them about what a record is made of or what a write to it
 * looks like. `recordDetailFields.ts` adds only the two properties this page
 * edits that Review does not (punch Status, task Category).
 *
 * ## What the design draws that this page does not
 *
 * - Adding, removing or re-captioning photos, and a per-photo location: the
 *   console has no write path for any of them, and a photo's description is
 *   written only by `VisitProcessor.describeOnePhoto` (see CLAUDE.md).
 * - The Punch "Assignee response / manager / approver / dates" pickers: the
 *   console projection carries none of those fields, so they show as "—".
 *
 * ## Mac or PC
 *
 * A task's "Mark complete" names its shortcut in the reader's own keys —
 * ⌘ Return on a Mac, Ctrl Enter on a PC — from `lib/platform.ts`, which reads
 * the user agent. The handler listens for the same modifier the hint names.
 */

import { AssignToModal } from "../components/AssignToModal";
import { preferWorkspaceSuggestion } from "../components/assigneeSuggestion";
import { PhotoImage } from "../components/PhotoImage";
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MinusCircle,
  Bell,
  Building2,
  Calendar as CalendarIcon,
  CalendarCheck,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  Clock,
  Copy,
  ExternalLink,
  Eye,
  Flag,
  Folder,
  FolderOpen,
  Footprints,
  Hash,
  Link as LinkIcon,
  LoaderCircle,
  Lock,
  MapPin,
  Plus,
  MessageSquare,
  Sparkles,
  SquareCheck,
  Tag,
  TriangleAlert,
  User,
  UserCheck,
  UserCog,
  X,
} from "lucide-react";
import type { AutodeskIssueSync, ConsoleRow, ItemEditDirectoryOption, ItemEvidenceRow } from "../api";
import { fetchAutodeskSubtypes, fetchItemAssignees, itemEditCategory } from "../api";
import type { AutodeskSubtypeOption } from "../../../../backend/src/shared/autodeskSubtypeOptions";
import { usePropertyState, type PropertyState } from "../components/PropertyRows";
import type { ReviewField } from "./ReviewQueue";
import { copyToClipboard } from "../lib/clipboard";
import { NO_AUTOFILL } from "../lib/noAutofill";
import { shortcutKeys } from "../lib/platform";
import { MONTHS, initialsOf, whenStamp } from "../components/chrome";
import {
  ObservationTypeMark,
  TradeMark,
} from "../../../../backend/src/frontend/components/record/PropertyAppearance";
import { RD } from "../tokens";
import {
  buildLocationTree,
  normalizeLocationQuery,
  visibleLocationRows,
  type LocationNode,
  type LocationRow,
} from "../../../../backend/src/frontend/components/shared/location/locationTree";
import { PROCORE_MARK_URL } from "../assets/procoreMark";
import { DEFAULT_PROCORE_OBSERVATION_TYPE_OPTIONS } from "../../../../backend/src/shared/procoreDailyLog";
import { TIME_STEPS, displayTime, parseStoredTime, parseTypedTime } from "../../../../backend/src/shared/timeOfDay";
import {
  accIssueTypeLabel,
  extendRecordFields,
  isAutodesk,
  isMobileObservation,
  optionLabel,
  recordKind,
  recordSections,
  valueKey,
  type RecordKind,
} from "./recordDetailFields";

// ---------------------------------------------------------------------------
// The design's palette
// ---------------------------------------------------------------------------

const INK = RD.ink;
const SUB = RD.sub;
const TER = RD.ter;
const QUIET = RD.quiet;
const CARD = RD.card;
const BG = RD.bg;
const SEC = RD.sec;
const SUNK = RD.sunk;
const LINE = "rgba(20,17,12,0.10)";
const LINE2 = "rgba(20,17,12,0.16)";
const HAIR = "rgba(20,17,12,0.07)";
const GREEN = RD.green;
const GOLD = RD.gold;
const RED = RD.red;
const TONE = {
  info: RD.info,
  amber: RD.amber,
  orange: RD.orange,
  red: RED,
  green: GREEN,
};
const AVATAR = [RD.info, RD.green, RD.violet, RD.orange, RD.rose];
const MONO = "'JetBrains Mono', ui-monospace, 'SF Mono', monospace";

/** The tokens `PropertyAppearance` (the phone's own trade and type marks)
 *  colours with. The console does not load the phone's `sega.css`, so they
 *  are defined on this page's root — same values as `sega.css :root`. */
const PHONE_TOKENS = {
  "--color-info": RD.info,
  "--signal-green": GREEN,
  "--property-violet": RD.violet,
  "--property-due-soon": RD.orange,
  "--property-teal": RD.teal,
  "--destructive": RED,
  "--signal-amber": RD.amber,
  "--muted-foreground": SUB,
  "--card": CARD,
} as React.CSSProperties;

/** Hover reveals and focus rings — the parts inline styles cannot say. */
const RECORD_CSS = `
.rd-row .rd-cp { opacity: 0; transition: opacity .12s; }
.rd-row:hover .rd-cp, .rd-row .rd-cp:focus-visible { opacity: 1; }
.rd-row .rd-cp.rd-rest { opacity: .45; }
.rd-row:hover .rd-cp.rd-rest { opacity: 1; }
.rd-cp:hover, .rd-trig:hover, .rd-btn:hover, .rd-cal-nav:hover { background: ${SEC} !important; }
.rd-trig:hover { color: ${INK}; }
.rd-trig:focus-visible { box-shadow: 0 0 0 2px rgba(244,190,46,.45); }
.rd-cal-day:hover { box-shadow: inset 0 0 0 1px ${LINE2}; }
.rd-done:hover { filter: brightness(.97); }
.rd-link:hover { text-decoration: underline; text-underline-offset: 3px; }
.rd-done:active { transform: scale(.97); }
.rd-photo:hover .rd-photo-img { filter: brightness(.96); }
.rd-thumb { opacity: .7; transition: opacity .12s, box-shadow .12s; }
.rd-thumb:hover, .rd-thumb[aria-pressed="true"] { opacity: 1; }
.rd-detach:hover { background: ${RED} !important; }
.rd-step:hover { background: ${CARD} !important; }
.rd-add:hover { background: ${SEC} !important; }
.rd-noscroll { scrollbar-width: none; }
[data-location-pin] { transition: box-shadow .12s; }
[data-location-pin][data-pinned="edge"] { box-shadow: 0 1px 0 ${LINE}, 0 6px 10px -8px rgba(22,18,10,.35); }
.rd-noscroll::-webkit-scrollbar { display: none; }
.rd-title::placeholder, .rd-desc::placeholder, .rd-search::placeholder { color: ${TER}; opacity: 1; }
@keyframes rdPopIn { from { opacity: 0; transform: translateY(-4px) scale(.98); } to { opacity: 1; transform: none; } }
@keyframes rdFadeIn { from { opacity: 0; } to { opacity: 1; } }
.rd-grid { display: grid; grid-template-columns: minmax(0,1fr) 280px; gap: 56px; align-items: start; }
@media (max-width: 980px) {
  .rd-grid { grid-template-columns: minmax(0,1fr); gap: 40px; }
  .rd-side { position: static !important; }
}
`;

/** The editors' own stylesheet, for a host that draws `Property` outside
 *  this page (the review card). */
export function PropertyEditorStyles() {
  return <style>{RECORD_CSS}</style>;
}

const KIND_CRUMB: Record<RecordKind, { plural: string; icon: React.ReactNode; noun: string }> = {
  observation: { plural: "Observations", noun: "observation", icon: <Eye size={13} color={TONE.amber} strokeWidth={2} aria-hidden /> },
  punch: { plural: "Punch list", noun: "punch item", icon: <TriangleAlert size={13} color={TONE.red} strokeWidth={2} aria-hidden /> },
  task: { plural: "Tasks", noun: "task", icon: <SquareCheck size={13} color={TONE.info} strokeWidth={2} aria-hidden /> },
};

/** On an Autodesk project an observation and a punch item are both an ACC
 *  Issue, and the page calls them that — the phone's word (LED-766's
 *  `walkPhotoChip`, `typeChipModel`). */
const ISSUE_CRUMB = { plural: "Issues", noun: "issue", icon: <Eye size={13} color={TONE.amber} strokeWidth={2} aria-hidden /> };

/** One read of a project's ACC Type › Subtype catalog per page load: it is a
 *  live ACC call, and every Issue of the project shares the answer. */
const accCatalogs = new Map<string, Promise<AutodeskSubtypeOption[]>>();
function accCatalog(jobSiteId: string, search: string): Promise<AutodeskSubtypeOption[]> {
  let read = accCatalogs.get(jobSiteId);
  if (!read) {
    read = fetchAutodeskSubtypes(jobSiteId, search).catch(() => {
      accCatalogs.delete(jobSiteId);
      return [];
    });
    accCatalogs.set(jobSiteId, read);
  }
  return read;
}

export type SyncTone = "ok" | "wait" | "bad";
export type SyncProvider = "procore" | "autodesk";

/** A record's sync as the page draws it — the sidebar row, its hover card and
 *  the activity entry all read this one shape, for Procore and ACC alike. */
export interface SyncLook {
  provider: SyncProvider;
  tone: SyncTone;
  /** "Synced to Procore" — the card's headline. */
  label: string;
  /** "Synced" — the sidebar row's word. */
  short: string;
  /** The activity entry's bold part. */
  event: string;
  /** "Last synced", "Queued", "Failed" — what `at` was. */
  whenKey: string;
  /** ISO. The sync's own instant, absent when the record never recorded one. */
  at?: string;
  /** "Observation #" plus the number — the system's human number only, never its API id. */
  ref?: string;
  url?: string;
  reason?: string;
}

const PROVIDER: Record<SyncProvider, { name: string; view: string }> = {
  procore: { name: "Procore", view: "View in Procore" },
  autodesk: { name: "Autodesk", view: "View in ACC" },
};

const PROCORE_NOUN: Record<RecordKind, string> = { observation: "Observation", punch: "Punch item", task: "Task" };

/** The noun before a record's number in that system: "Task", "Manpower",
 *  an ACC "Issue" or, for a daily-log row in ACC, its "Daily report". */
export function syncNoun(item: ConsoleRow, provider: SyncProvider): string {
  if (provider === "procore") return procoreNoun(item);
  return item.kind === "log" && !isMobileObservation(item) ? "Daily report" : "Issue";
}

/** "Manpower", "Daily log entry" — what Procore calls a daily-log row. */
function procoreNoun(item: ConsoleRow): string {
  if (item.kind !== "log" || isMobileObservation(item)) return PROCORE_NOUN[recordKind(item)];
  const words = (item.category ?? "").replace(/[_-]+/g, " ").trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "Daily log entry";
}

const BLOCKED_REASON: Record<string, string> = {
  deleted_in_procore: "This record was deleted in Procore.",
  deleted_in_led_ai: "This record was deleted in Led AI.",
};

const iso = (value: unknown) => (value ? String(value instanceof Date ? value.toISOString() : value) : undefined);

/** `null` when there is nothing to say: an Autodesk record, a record never
 *  pushed, or one skipped on purpose (not approved, no Procore project). */
export function procoreSyncLook(item: ConsoleRow): SyncLook | null {
  const sync = item.procoreSync;
  if (isAutodesk(item) || !sync?.status) return null;
  const number = sync.remoteNumber ?? (item.kind === "punch" ? item.punchNumber : undefined);
  const base = {
    provider: "procore" as const,
    ref: number ? `${procoreNoun(item)} #${number}` : undefined,
    url: sync.remoteUrl,
  };
  switch (sync.status) {
    case "synced":
      return { ...base, tone: "ok", label: "Synced to Procore", short: "Synced", event: "Synced to Procore", whenKey: "Last synced", at: iso(sync.syncedAt ?? sync.attemptedAt) };
    case "partial":
      return { ...base, tone: "wait", label: "Partly synced to Procore", short: "Partly synced", event: "Partly synced to Procore", whenKey: "Last synced", at: iso(sync.syncedAt ?? sync.attemptedAt), reason: sync.lastError };
    case "pending":
    case "syncing":
      return { ...base, tone: "wait", label: "Procore sync pending", short: "Syncing…", event: "Procore sync queued", whenKey: "Queued", at: iso(sync.attemptedAt) };
    case "failed":
    case "conflict":
    case "blocked":
      return {
        ...base,
        tone: "bad",
        label: sync.status === "conflict" ? "Procore sync conflict" : "Procore sync failed",
        short: "Sync failed",
        event: sync.status === "conflict" ? "Procore sync conflict" : "Procore sync failed",
        whenKey: "Failed",
        at: iso(sync.attemptedAt),
        reason: sync.lastError || (sync.reason && BLOCKED_REASON[sync.reason]) || "Procore did not say why.",
      };
    default:
      return null;
  }
}

/** An ACC record's sync, in the Procore row's words — what the phone's sync
 *  chip says (`readAutodeskSyncForDisplay`), with the failure reason. */
export function autodeskSyncLook(sync: AutodeskIssueSync | undefined): SyncLook | null {
  if (!sync?.status) return null;
  const base = { provider: "autodesk" as const, ref: sync.number ? `Issue #${sync.number}` : undefined, url: sync.url };
  if (sync.status === "synced") {
    return { ...base, tone: "ok", label: "Synced to ACC", short: "Synced", event: "Synced to ACC", whenKey: "Last synced", at: iso(sync.syncedAt) };
  }
  if (sync.status === "failed") {
    return { ...base, tone: "bad", label: "ACC sync failed", short: "Sync failed", event: "ACC sync failed", whenKey: "Failed", reason: sync.error || "ACC did not say why." };
  }
  return { ...base, tone: "wait", label: "ACC sync pending", short: "Syncing…", event: "ACC sync queued", whenKey: "Queued" };
}

/** The one sync a record shows: ACC's on an Autodesk project, else Procore's. */
export function recordSyncLook(item: ConsoleRow, autodesk?: AutodeskIssueSync): SyncLook | null {
  return isAutodesk(item) ? autodeskSyncLook(autodesk) : procoreSyncLook(item);
}

/** Ink, soft disc and icon per tone — the design's three sync states. */
export const SYNC_TONE: Record<SyncTone, { fg: string; bg: string; Icon: typeof CircleCheck; pulse: boolean }> = {
  ok: { fg: RD.syncOk, bg: RD.syncOkSoft, Icon: CircleCheck, pulse: false },
  wait: { fg: RD.syncWait, bg: RD.syncWaitSoft, Icon: LoaderCircle, pulse: true },
  bad: { fg: RD.syncBad, bg: RD.syncBadSoft, Icon: CircleAlert, pulse: false },
};

/** The activity entry a sync contributes, or `null`. Its `at` lets the caller
 *  slot it among the history by time. */
export function syncActivityRow(look: SyncLook | null): (RecordActivityRow & { at?: string }) | null {
  if (!look) return null;
  return {
    id: `${look.provider}-sync`,
    sentence: look.event,
    detail: look.tone === "bad" ? look.reason : look.ref,
    when: look.at ? whenStamp(look.at) : "",
    sync: look.tone,
    at: look.at,
  };
}

/** The row's own rules, so it reads the same outside this page (the daily
 *  log's field-table record and its side editor). */
const SYNC_CSS = `
@keyframes rdPulse { 0%, 100% { opacity: 1; } 50% { opacity: .35; } }
@keyframes rdFadeIn { from { opacity: 0; } to { opacity: 1; } }
.rd-sync:hover, .rd-sync:focus-visible { background: ${SEC}; }
.rd-sync-retry:hover:not(:disabled) { background: ${RD.syncBad} !important; color: ${RD.white} !important; }
`;

function AutodeskGlyph({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden style={{ display: "block", flex: "none" }}>
      <path
        d="m.129 20.202 14.7-9.136h7.625c.235 0 .445.188.445.445 0 .21-.092.305-.21.375l-7.222 4.323c-.47.283-.633.845-.633 1.265l-.008 2.725H24V4.362a.561.561 0 0 0-.585-.562h-8.752L0 12.893V20.2h.129z"
        fill={INK}
      />
    </svg>
  );
}

export type RetrySync = () => Promise<{ ok: boolean; error?: string }>;

/** The sync row: the system's mark, its state and — on hover or focus — the
 *  card with when it last moved, the record's number there, the failure
 *  reason and, for a failure, Retry sync. */
export function SyncRow({
  look,
  onRetry,
  align = "left",
  triggerStyle,
}: {
  look: SyncLook;
  /** Offered on a failed sync only; the caller re-reads the record after. */
  onRetry?: RetrySync;
  /** Which edge the card hangs from — "right" where the row sits at a panel's right. */
  align?: "left" | "right";
  triggerStyle?: React.CSSProperties;
}) {
  const [open, setOpen] = useState(false);
  const [retry, setRetry] = useState<{ busy: boolean; error?: string }>({ busy: false });
  const tone = SYNC_TONE[look.tone];
  const provider = PROVIDER[look.provider];
  const testId = `record-${look.provider}`;
  // The card lines up with the row's hover tint, which bleeds 8px past the
  // text unless the caller pinned the row's margin (a chip, a panel header).
  const inset = triggerStyle?.margin === 0 ? 0 : -8;
  const pulse = tone.pulse ? "rdPulse 1.4s ease-in-out infinite" : "none";
  const rowStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: 9, height: 32, padding: "0 8px", margin: "0 -8px", borderRadius: 8, fontSize: 13.5, color: INK, textDecoration: "none", outline: "none", ...triggerStyle };
  const inner = (
    <>
      {look.provider === "procore" ? (
        <img src={PROCORE_MARK_URL} alt="" style={{ width: 15, height: 15, borderRadius: 3, display: "block", flex: "none" }} />
      ) : (
        <AutodeskGlyph size={15} />
      )}
      <span style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 7 }}>
        <span style={{ color: SUB }}>{provider.name}</span>
        <span style={{ display: "inline-flex", animation: pulse }}>
          <tone.Icon size={13} color={tone.fg} strokeWidth={2.2} aria-hidden />
        </span>
        <span data-testid={`${testId}-short`} style={{ minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{look.short}</span>
      </span>
      {look.url && <ExternalLink size={13} color={SUB} strokeWidth={2} aria-hidden />}
    </>
  );
  const canRetry = look.tone === "bad" && onRetry;
  const runRetry = async () => {
    if (!onRetry || retry.busy) return;
    setRetry({ busy: true });
    let result: { ok: boolean; error?: string };
    try {
      result = await onRetry();
    } catch (error) {
      result = { ok: false, error: error instanceof Error ? error.message : undefined };
    }
    setRetry(result.ok ? { busy: false } : { busy: false, error: result.error || "Could not retry the sync." });
  };
  return (
    <section
      aria-label={provider.name}
      data-testid={testId}
      data-tone={look.tone}
      style={{ position: "relative", zIndex: open ? 30 : "auto" }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <style>{SYNC_CSS}</style>
      {look.url ? (
        <a className="rd-sync" href={look.url} target="_blank" rel="noreferrer" title={look.label} style={rowStyle}>{inner}</a>
      ) : (
        <div className="rd-sync" tabIndex={0} title={look.label} style={rowStyle}>{inner}</div>
      )}
      {open && (
        // The 4px above the card is padding, not a gap, so the pointer can
        // travel from the row to "View in Procore" without leaving the hover.
        <div style={{ position: "absolute", ...(align === "right" ? { right: inset } : { left: inset }), top: "100%", zIndex: 40, paddingTop: 4 }}>
        <div
          data-testid={`${testId}-card`}
          style={{ width: 270, background: CARD, border: `1px solid ${LINE2}`, borderRadius: 12, boxShadow: "0 14px 30px rgba(22,18,10,.18)", padding: "12px 14px", animation: "rdFadeIn .12s both", textAlign: "left" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5 }}>
            <span style={{ display: "inline-flex", animation: pulse }}>
              <tone.Icon size={15} color={tone.fg} strokeWidth={2} aria-hidden />
            </span>
            <span style={{ fontWeight: 600, color: tone.fg }}>{look.label}</span>
          </div>
          {look.at && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 12.5, color: SUB }}>
              <Clock size={15} color={SUB} strokeWidth={2} aria-hidden />
              <span>
                {`${look.whenKey} `}
                <span style={{ color: INK }}>{whenStamp(look.at)}</span>
              </span>
            </div>
          )}
          {look.ref && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, fontSize: 12.5, color: SUB }}>
              <Hash size={13} color={SUB} strokeWidth={2} aria-hidden />
              <span style={{ color: INK }}>{look.ref}</span>
            </div>
          )}
          {look.reason && (
            <div data-testid={`${testId}-reason`} style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${HAIR}`, fontSize: 12.5, lineHeight: 1.45, color: look.tone === "bad" ? RD.syncBad : SUB, overflowWrap: "anywhere" }}>
              {look.reason}
            </div>
          )}
          {canRetry && (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
              <button
                type="button"
                className="rd-sync-retry"
                data-testid={`${testId}-retry`}
                disabled={retry.busy}
                onClick={() => void runRetry()}
                style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 6, height: 28, padding: "0 10px", borderRadius: 7, border: `1px solid ${RD.syncBad}`, background: RD.syncBadSoft, color: RD.syncBad, fontSize: 12.5, fontWeight: 600, fontFamily: "inherit", cursor: retry.busy ? "default" : "pointer", opacity: retry.busy ? 0.6 : 1 }}
              >
                {retry.busy ? "Retrying…" : "Retry sync"}
              </button>
              {retry.error && (
                <span data-testid={`${testId}-retry-error`} role="alert" style={{ fontSize: 12, color: RD.syncBad, lineHeight: 1.4 }}>{retry.error}</span>
              )}
            </div>
          )}
          {look.url && look.tone !== "bad" && (
            <a href={look.url} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10, fontSize: 12.5, fontWeight: 600, color: INK, textDecoration: "none" }}>
              {provider.view}
              <ExternalLink size={13} color={SUB} strokeWidth={2} aria-hidden />
            </a>
          )}
        </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Marks
// ---------------------------------------------------------------------------

interface StatusLook {
  color: string;
  /** Wedge fraction of a ring, or a filled disc carrying a mark. */
  p?: number;
  fill?: "check" | "x";
  dashed?: boolean;
}

const STATUS_LOOK: Record<string, StatusLook> = {
  draft: { color: SUB, dashed: true },
  not_started: { color: SUB, dashed: true },
  open: { color: TONE.amber, p: 0 },
  initiated: { color: TONE.amber, p: 0.25 },
  pending: { color: TONE.amber, p: 0.25 },
  in_progress: { color: TONE.info, p: 0.5 },
  ready_for_review: { color: GREEN, p: 0.75 },
  not_accepted: { color: RED, fill: "x" },
  rejected: { color: RED, fill: "x" },
  voided: { color: SUB, fill: "x" },
  verified: { color: GREEN, fill: "check" },
  approved: { color: GREEN, fill: "check" },
  closed: { color: INK, fill: "check" },
  completed: { color: INK, fill: "check" },
};

function lookFor(item: ConsoleRow, value: string): StatusLook {
  const key = valueKey(value);
  // Under ACC an observation's `not_accepted` is ACC's Pending — a
  // quarter-filled amber ring, not a rejection.
  if (isAutodesk(item) && key === "not_accepted") return STATUS_LOOK.pending!;
  return STATUS_LOOK[key] ?? { color: SUB, p: 0 };
}

export function StatusMark({ look, size = 16 }: { look: StatusLook | null; size?: number }) {
  const c = size / 2;
  const r = c - 1.3;
  if (!look || look.dashed) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden style={{ flex: "none", display: "block" }}>
        <circle cx={c} cy={c} r={r} stroke={look?.color ?? SUB} strokeWidth={1.6} strokeDasharray="2.4 2.4" fill="none" />
      </svg>
    );
  }
  if (look.fill) {
    const d = look.fill === "x"
      ? `M${c * 0.68} ${c * 0.68} L${c * 1.32} ${c * 1.32} M${c * 1.32} ${c * 0.68} L${c * 0.68} ${c * 1.32}`
      : `M${c * 0.62} ${c * 1.04} L${c * 0.9} ${c * 1.32} L${c * 1.4} ${c * 0.74}`;
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden style={{ flex: "none", display: "block" }}>
        <circle cx={c} cy={c} r={r} fill={look.color} />
        <path d={d} stroke={CARD} strokeWidth={size * 0.13} strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    );
  }
  const p = look.p ?? 0;
  const a = p * 2 * Math.PI;
  const ri = r - 2.8;
  const x = c + ri * Math.sin(a);
  const y = c - ri * Math.cos(a);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden style={{ flex: "none", display: "block" }}>
      <circle cx={c} cy={c} r={r} stroke={look.color} strokeWidth={1.8} fill="none" />
      {p > 0 && (
        <path d={`M${c} ${c} L${c} ${c - ri} A${ri} ${ri} 0 ${a > Math.PI ? 1 : 0} 1 ${x} ${y} Z`} fill={look.color} />
      )}
    </svg>
  );
}

const PRIORITY_LEVEL: Record<string, number> = { low: 1, medium: 2, high: 3, urgent: 4 };

export function PriorityMark({ level, size = 16 }: { level: number; size?: number }) {
  if (level === 4) {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden style={{ flex: "none", display: "block" }}>
        <rect x={1.5} y={1.5} width={13} height={13} rx={3.5} fill={RED} />
        <path d="M8 4.5v4.2" stroke={CARD} strokeWidth={2} strokeLinecap="round" />
        <circle cx={8} cy={11.6} r={1.1} fill={CARD} />
      </svg>
    );
  }
  const color = level === 3 ? RED : level === 2 ? TONE.amber : SUB;
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden style={{ flex: "none", display: "block" }}>
      {[5, 8, 11].map((h, i) => (
        <rect key={h} x={2.5 + i * 4.5} y={14 - h} width={3} height={h} rx={1} fill={i < level ? color : "rgba(20,17,12,0.14)"} />
      ))}
    </svg>
  );
}

function avatarOf(name: string) {
  let h = 5381;
  for (let i = 0; i < name.length; i++) h = ((h << 5) + h + name.charCodeAt(i)) | 0;
  const color = AVATAR[Math.abs(h) % AVATAR.length]!;
  return { initials: initialsOf(name), color, tile: `color-mix(in srgb, ${color} 17%, ${CARD})` };
}

function Avatar({ name, size }: { name: string; size: number }) {
  const look = avatarOf(name);
  return (
    <span
      aria-hidden
      style={{
        flex: "none",
        width: size,
        height: size,
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: look.tile,
        color: look.color,
        fontSize: size * 0.37,
        fontWeight: 700,
      }}
    >
      {look.initials}
    </span>
  );
}

const DASHED = { strokeDasharray: "2.5 2" } as const;

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

function parseDay(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function isoDay(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function sameDay(a: Date | null, b: Date | null): boolean {
  return !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function fmtDay(date: Date | null): string {
  return date ? `${MONTHS[date.getMonth()]!.slice(0, 3)} ${date.getDate()}, ${date.getFullYear()}` : "";
}

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

function useCopy() {
  const [copied, setCopied] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);
  const copy = async (key: string, text: string) => {
    if (!text) return;
    if (!(await copyToClipboard(text))) return;
    setCopied(key);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(null), 1400);
  };
  return { copied, copy };
}

function CopyButton({
  label,
  done,
  hasValue = true,
  /** Shown faintly at rest (a property row's value) rather than only on
   *  hover (the description, a caption, a detail) — the design's two cases. */
  restVisible = false,
  size = 24,
  style,
  onCopy,
}: {
  label: string;
  done: boolean;
  hasValue?: boolean;
  restVisible?: boolean;
  size?: number;
  style?: React.CSSProperties;
  onCopy: () => void;
}) {
  return (
    <button
      type="button"
      className={`rd-cp${hasValue && restVisible ? " rd-rest" : ""}`}
      data-testid="record-copy"
      aria-label={done ? `Copied ${label}` : `Copy ${label}`}
      title={done ? "Copied" : `Copy ${label}`}
      disabled={!hasValue}
      onClick={(event) => {
        event.stopPropagation();
        onCopy();
      }}
      style={{
        flex: "none",
        width: size,
        height: size,
        borderRadius: 6,
        border: "none",
        background: "transparent",
        padding: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: hasValue ? "pointer" : "default",
        visibility: hasValue ? "visible" : "hidden",
        ...style,
      }}
    >
      {done ? <Check size={13} color={RD.copied} strokeWidth={2.4} aria-hidden /> : <Copy size={13} color={SUB} strokeWidth={2} aria-hidden />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Popover rows
// ---------------------------------------------------------------------------

interface Row {
  id: string;
  kind: "option" | "branch" | "leaf" | "location" | "person" | "candidate" | "unassigned";
  label: string;
  sub?: string;
  mark?: React.ReactNode;
  count?: number;
  selected?: boolean;
  muted?: boolean;
  expanded?: boolean;
  /** A leaf drawn inside an expanded branch, with the tree's guide line. */
  nested?: boolean;
  lastNested?: boolean;
  /** A branch whose subtree holds the current value. */
  holdsValue?: boolean;
  /** A location's level in the project tree, and whether it has children. */
  depth?: number;
  branch?: boolean;
  pct?: number;
  top?: boolean;
  /** A person's face drawn by the host instead of this page's initials. */
  face?: React.ReactNode;
  pick: () => void;
  expand?: (open: boolean) => void;
}

/** A location row, and how far down each pinned level sits. */
const LOCATION_ROW = 38;
/** Levels deeper than this scroll away rather than pin. */
const LOCATION_PINNED_LEVELS = 3;

function rowBackground(selected: boolean | undefined, active: boolean): string {
  if (selected) return active ? RD.selActive : SEC;
  return active ? RD.rowActive : "transparent";
}

function PopoverRow({
  row,
  active,
  keyboard,
  onHover,
}: {
  row: Row;
  active: boolean;
  keyboard: boolean;
  onHover: () => void;
}) {
  const ring = active && keyboard ? `inset 0 0 0 2px ${GOLD}` : "none";
  const check = row.selected ? <Check size={15} color={INK} strokeWidth={2.4} aria-hidden /> : null;
  const common: React.HTMLAttributes<HTMLDivElement> & Record<string, unknown> = {
    "data-testid": "record-option",
    "data-active": active || undefined,
    "aria-selected": !!row.selected,
    onMouseMove: onHover,
    onMouseDown: (event: React.MouseEvent) => event.preventDefault(),
    onClick: () => row.pick(),
  };

  if (row.kind === "branch") {
    return (
      <div
        {...common}
        role="treeitem"
        aria-expanded={!!row.expanded}
        style={{ display: "flex", alignItems: "center", gap: 8, height: 36, padding: "0 10px 0 8px", flex: "none", borderRadius: 8, cursor: "pointer", background: rowBackground(false, active), boxShadow: ring }}
      >
        <span style={{ width: 16, height: 16, display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "transform .18s", transform: `rotate(${row.expanded ? 90 : 0}deg)` }}>
          <ChevronRight size={16} color={SUB} strokeWidth={2} aria-hidden />
        </span>
        {row.mark && <span style={{ width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{row.mark}</span>}
        <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.label}</span>
        {row.holdsValue && <Check size={14} color={INK} strokeWidth={2.4} aria-hidden />}
        {row.count !== undefined && <span style={{ minWidth: 14, textAlign: "right", fontFamily: MONO, fontSize: 11, fontWeight: 600, color: SUB }}>{row.count}</span>}
      </div>
    );
  }

  // The phone's location tree (`LocationTreePicker`): a chevron that folds a
  // level, a folder or a pin, the guide curve into a place, and the place
  // picked in the phone's green. A level that is itself a place is picked
  // by its name; one that only groups places folds by its name too.
  if (row.kind === "location") {
    const depth = row.depth ?? 0;
    const tint = row.selected ? (active ? RD.greenSoftActive : RD.greenSoft) : active ? RD.rowActive : CARD;
    return (
      <div
        {...common}
        role="treeitem"
        aria-level={depth + 1}
        aria-expanded={row.branch ? !!row.expanded : undefined}
        title={row.sub}
        data-location-path={row.sub}
        data-location-depth={depth}
        style={{ scrollMarginTop: Math.min(depth, LOCATION_PINNED_LEVELS) * LOCATION_ROW, display: "flex", alignItems: "center", height: LOCATION_ROW, paddingLeft: Math.min(depth, 6) * 14, paddingRight: 10, flex: "none", borderRadius: 8, cursor: "pointer", background: tint, boxShadow: ring }}
      >
        {row.branch ? (
          <span
            role="button"
            data-testid="location-toggle"
            aria-label={`${row.expanded ? "Collapse" : "Expand"} ${row.sub ?? row.label}`}
            onClick={(event) => {
              event.stopPropagation();
              row.expand?.(!row.expanded);
            }}
            style={{ width: 30, alignSelf: "stretch", flex: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 6 }}
          >
            <ChevronRight size={15} color={SUB} strokeWidth={2} aria-hidden style={{ transition: "transform .18s", transform: `rotate(${row.expanded ? 90 : 0}deg)` }} />
          </span>
        ) : (
          <span aria-hidden style={{ position: "relative", width: 30, alignSelf: "stretch", flex: "none" }}>
            <span style={{ position: "absolute", left: 15, top: 0, height: LOCATION_ROW / 2, width: 8, borderLeft: `1px solid ${LINE2}`, borderBottom: `1px solid ${LINE2}`, borderBottomLeftRadius: 5 }} />
          </span>
        )}
        <span style={{ width: 20, height: 20, marginRight: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
          {row.branch ? (
            row.expanded ? <FolderOpen size={16} color={SUB} strokeWidth={2} aria-hidden /> : <Folder size={16} color={SUB} strokeWidth={2} aria-hidden />
          ) : (
            <MapPin size={15} color={SUB} strokeWidth={2} aria-hidden />
          )}
        </span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 550, color: INK, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.label}</span>
        {row.selected ? (
          <Check size={15} color={GREEN} strokeWidth={2.4} aria-hidden style={{ flex: "none", marginLeft: 8 }} />
        ) : row.holdsValue ? (
          <span aria-hidden title="The location is inside" style={{ flex: "none", width: 6, height: 6, marginLeft: 8, marginRight: 4, borderRadius: "50%", background: GREEN }} />
        ) : null}
      </div>
    );
  }

  if (row.kind === "leaf") {
    return (
      <div style={{ flex: "none" }}>
        <div
          {...common}
          role="treeitem"
          style={{ display: "flex", alignItems: "center", gap: 8, height: 36, padding: `0 10px 0 ${row.nested ? 14 : 10}px`, flex: "none", borderRadius: 8, cursor: "pointer", background: rowBackground(row.selected, active), boxShadow: ring }}
        >
          {row.nested && (
            <span aria-hidden style={{ position: "relative", width: 16, alignSelf: "stretch", flex: "none" }}>
              <span style={{ position: "absolute", left: 7, top: -2, height: 20, width: 7, borderLeft: `1px solid ${LINE2}`, borderBottom: `1px solid ${LINE2}`, borderBottomLeftRadius: 4 }} />
              <span style={{ position: "absolute", left: 7, top: 18, bottom: -2, borderLeft: `1px solid ${row.lastNested ? "transparent" : LINE2}` }} />
            </span>
          )}
          <span style={{ width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{row.mark}</span>
          <span style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "baseline", gap: 6, whiteSpace: "nowrap", overflow: "hidden" }}>
            <span style={{ fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis" }}>{row.label}</span>
            {row.sub && <span style={{ fontSize: 11.5, color: TER, overflow: "hidden", textOverflow: "ellipsis" }}>{row.sub}</span>}
          </span>
          {check}
        </div>
      </div>
    );
  }

  if (row.kind === "unassigned") {
    return (
      <div
        {...common}
        role="option"
        style={{ display: "flex", alignItems: "center", gap: 10, height: 40, padding: "0 10px", flex: "none", borderRadius: 8, cursor: "pointer", background: rowBackground(row.selected, active), boxShadow: ring }}
      >
        <span style={{ width: 28, height: 28, borderRadius: "50%", border: `1.5px dashed ${LINE2}`, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
          <User size={14} color={SUB} strokeWidth={2} aria-hidden />
        </span>
        <span style={{ flex: 1, fontSize: 13.5, fontWeight: 500, color: SUB }}>Unassigned</span>
        {check}
      </div>
    );
  }

  if (row.kind === "person" || row.kind === "candidate") {
    const look = avatarOf(row.label);
    const candidate = row.kind === "candidate";
    return (
      <div
        {...common}
        role="option"
        style={{ display: "flex", alignItems: "center", gap: 10, height: candidate ? 48 : 44, padding: "0 10px", flex: "none", borderRadius: 8, cursor: "pointer", background: rowBackground(row.selected, active), boxShadow: ring }}
      >
        {row.face ? (
          <span aria-hidden style={{ flex: "none", width: 28, height: 28, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", boxShadow: row.top ? `0 0 0 1.5px ${GREEN}` : "none" }}>
            {row.face}
          </span>
        ) : (
        <span
          aria-hidden
          style={{ flex: "none", width: 28, height: 28, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", background: look.tile, color: look.color, fontSize: 10.4, fontWeight: 700, boxShadow: row.top ? `0 0 0 1.5px ${GREEN}` : "none" }}
        >
          {look.initials}
        </span>
        )}
        <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: candidate ? 2 : 1 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, fontWeight: 600, lineHeight: "17px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {row.label}
            {row.top && <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: 1, color: GREEN }}>TOP</span>}
          </span>
          {row.sub && <span style={{ fontSize: 11, lineHeight: "14px", color: SUB, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.sub}</span>}
        </span>
        {candidate && row.pct !== undefined && (
          <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5 }}>
            <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700 }}>{`${row.pct}%`}</span>
            <span style={{ width: 48, height: 3, borderRadius: 2, background: SEC, overflow: "hidden", display: "block" }}>
              <span style={{ display: "block", height: "100%", width: `${row.pct}%`, background: row.top ? GREEN : "rgba(20,17,12,0.35)" }} />
            </span>
          </span>
        )}
        {check}
      </div>
    );
  }

  return (
    <div
      {...common}
      role="option"
      style={{ display: "flex", alignItems: "center", gap: 10, height: 36, padding: "0 10px", flex: "none", borderRadius: 8, cursor: "pointer", background: rowBackground(row.selected, active), boxShadow: ring }}
    >
      <span style={{ width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{row.mark}</span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 500, color: row.muted ? SUB : INK, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.label}</span>
      {check}
    </div>
  );
}

function SearchBox({
  value,
  placeholder,
  hotkey,
  onChange,
}: {
  value: string;
  placeholder: string;
  /** The single key that opens this property, shown while the box is empty. */
  hotkey?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        height: 40,
        padding: "0 6px 0 10px",
        margin: "-6px -6px 2px",
        borderBottom: `1px solid ${LINE}`,
        flex: "none",
      }}
    >
      <input
        // A popover opens to type into: focus lands here, not on the trigger.
        autoFocus
        className="rd-search"
        data-testid="record-search"
        type="text"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        {...NO_AUTOFILL}
        onChange={(event) => onChange(event.target.value)}
        // As well as onChange: React 19 under happy-dom raises only `input`.
        onInput={(event) => onChange((event.target as HTMLInputElement).value)}
        style={{ flex: 1, minWidth: 0, border: 0, outline: "none", background: "transparent", fontSize: 14, color: INK, padding: 0, fontFamily: "inherit" }}
      />
      {value && (
        <button
          type="button"
          aria-label="Clear search"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onChange("")}
          style={{ width: 18, height: 18, borderRadius: "50%", border: 0, background: SUB, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0, flex: "none" }}
        >
          <X size={11} color={CARD} strokeWidth={2.6} aria-hidden />
        </button>
      )}
      {!value && hotkey && (
        <kbd
          data-testid="record-search-hotkey"
          style={{ fontFamily: MONO, fontSize: 10, fontWeight: 600, lineHeight: 1, color: TER, background: BG, border: `1px solid ${LINE2}`, borderRadius: 5, padding: "4px 6px", flex: "none" }}
        >
          {hotkey}
        </kbd>
      )}
    </div>
  );
}

function SectionLabel({ left, right }: { left: React.ReactNode; right?: string }) {
  return (
    <div style={{ flex: "none", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, padding: "10px 10px 6px", fontFamily: MONO, fontSize: 9.5, fontWeight: 600, letterSpacing: 1.3, textTransform: "uppercase", color: SUB }}>
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>{left}</span>
      {right && <span style={{ color: TER, letterSpacing: 0.6 }}>{right}</span>}
    </div>
  );
}

function Empty({ text, onClear }: { text: string; onClear?: () => void }) {
  return (
    <div style={{ padding: "22px 12px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, fontSize: 13, color: SUB, textAlign: "center", textWrap: "pretty" } as React.CSSProperties}>
      {text}
      {onClear && (
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={onClear}
          style={{ border: 0, borderRadius: 8, background: SUNK, padding: "7px 12px", fontSize: 12.5, fontWeight: 600, color: INK, cursor: "pointer", fontFamily: "inherit" }}
        >
          Clear search
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One property: trigger + popover
// ---------------------------------------------------------------------------

type PickerShape = "list" | "tree" | "people" | "calendar" | "time" | "number";

export interface PropertySpec {
  field: ReviewField;
  shape: PickerShape;
  width: number;
  searchable: boolean;
  placeholder: string;
  unsetCopy: string;
  /** The single key that opens it from anywhere on the page (design v2). */
  hotkey?: string;
  /** `false` when the write path takes no empty value (a photo's location). */
  clearable?: boolean;
}

export function specFor(field: ReviewField): PropertySpec {
  const base = { field, searchable: true };
  if (field.inputType === "number") return { field, searchable: false, shape: "number", width: 220, placeholder: field.label, unsetCopy: `Set ${field.label.toLowerCase()}` };
  if (field.inputType === "time") return { ...base, shape: "time", width: 220, placeholder: "Type a time…", unsetCopy: `Set ${field.label.toLowerCase()}` };
  if (field.inputType === "date") return { field, searchable: false, placeholder: "", shape: "calendar", width: 272, unsetCopy: "Set due date" };
  if (field.reference === "assignee") return { ...base, shape: "people", width: 320, placeholder: "Assign to…", hotkey: "A", unsetCopy: "Unassigned" };
  if (field.reference === "location") return { ...base, shape: "tree", width: 320, placeholder: "Change location…", hotkey: "L", unsetCopy: "Set location" };
  if (field.key === "type") return { ...base, shape: "tree", width: 320, placeholder: "Change type…", hotkey: "T", unsetCopy: "Set type" };
  if (field.reference === "trade") return { ...base, shape: "list", width: 280, placeholder: "Change trade…", hotkey: "R", unsetCopy: "Add trade" };
  if (field.reference === "company") return { ...base, shape: "list", width: 280, placeholder: "Change company…", hotkey: "O", unsetCopy: "Add company" };
  if (field.key === "status") return { ...base, shape: "list", width: 260, placeholder: "Change status…", hotkey: "S", unsetCopy: "Set status" };
  if (field.key === "priority") return { ...base, shape: "list", width: 240, placeholder: "Change priority…", hotkey: "P", unsetCopy: "Set priority" };
  if (field.key === "taskCategory") return { ...base, shape: "list", width: 260, placeholder: "Change category…", hotkey: "C", unsetCopy: "Set category" };
  return { ...base, shape: "list", width: 240, placeholder: `Change ${field.label.toLowerCase()}…`, unsetCopy: `Set ${field.label.toLowerCase()}` };
}

/** Procore names a location by its whole path: `Tower A>Level 02>East Wing`. */
function locationParts(name: string): string[] {
  return name.split(/\s*[>›]\s*/).map((part) => part.trim()).filter(Boolean);
}

/**
 * An ACC Issue's location as its whole tree path ("Building A › Level 1 ›
 * Loading Dock"): ACC stores the leaf as the name and the path beside it. Only
 * while the path still ends in the value shown — a location picked since then
 * has no path here yet, and its own name is better than a stale path.
 */
function issuePath(item: Pick<ConsoleRow, "syncSource" | "location">, value: string): string {
  const path = item.location?.path;
  if (!isAutodesk(item) || !path || !value) return value;
  const leaf = locationParts(path).at(-1);
  return leaf && valueKey(leaf) === valueKey(locationParts(value).at(-1) ?? "") ? path : value;
}

/** "Quality - Deficiency" → ["Quality", "Deficiency"]; a type whose category
 *  and name agree is one word. */
function typeParts(value: string): [string, string] {
  const at = value.indexOf(" - ");
  if (at < 0) return [value, value];
  return [value.slice(0, at).trim(), value.slice(at + 3).trim()];
}

/** A type shows as its own name; the family is the tree branch above it. */
function typeLabel(value: string): string {
  return typeParts(value)[1];
}

/** What a host draws as the trigger instead of the record page's row. */
export interface PropertyFace {
  open: boolean;
  toggle: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  /** The value as words ("" when unset) and its full form (a whole path). */
  shown: string;
  full: string;
  isSet: boolean;
  unsetCopy: string;
}

export interface PropertyProps {
  spec: PropertySpec;
  state: PropertyState;
  item: ConsoleRow;
  today: Date;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCommit: () => void;
  copied: string | null;
  onCopy: (key: string, text: string) => void;
  /** A photo's location: a small inline trigger, no copy button. */
  compact?: boolean;
  /** Draw the trigger yourself (the review card's pill); the popover opens
   *  under it at `popoverOffset`. */
  renderTrigger?: (face: PropertyFace) => React.ReactNode;
  popoverOffset?: { left: number; top: number };
  /** A choice's mark in the list, in the host's vocabulary (the phone's,
   *  on the review card). `undefined` keeps this page's own mark. */
  optionMark?: (value: string, label: string) => React.ReactNode | undefined;
  /** A person's face in the people list. */
  personMark?: (name: string) => React.ReactNode;
  /** A choice's words when the field names its own (a daily log's options). */
  labelFor?: (value: string) => string | undefined;
  /**
   * Open the editor over the page rather than inside the property's own box.
   * A host whose properties sit in stacked cards (the review queue) needs it:
   * each card paints over the one before, so an editor opened inside a card
   * would be cut off by the next. Placed against the trigger, below it or —
   * without the room — above it, and kept inside the window.
   */
  portal?: boolean;
  /** Text the search opens with — the name-matching pill pre-fills a name
   *  the directory lacks (LED-1155). Every open otherwise starts empty. */
  openQuery?: string;
}

export function Property({ spec, state, item, today, open, onOpenChange, onCommit, copied, onCopy, compact, renderTrigger, popoverOffset, optionMark, personMark, labelFor, portal = false, openQuery }: PropertyProps) {
  const { field } = spec;
  const labelOf = (choice: string) => labelFor?.(choice) ?? optionLabel(item, field, choice);
  const value = state.values[field.key] ?? "";
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);
  const [keyboard, setKeyboard] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // The location tree opens unfolded, as the phone's does; what a reader
  // folds while browsing and while searching are kept apart, so clearing a
  // search returns the tree they left.
  const [foldedLocations, setFoldedLocations] = useState<ReadonlySet<string>>(() => new Set());
  const [foldedInSearch, setFoldedInSearch] = useState<ReadonlySet<string>>(() => new Set());
  const scrolledToLocation = useRef(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  /**
   * People are SEARCHED, never listed in full: `item-edit-options` leaves
   * them out on purpose (a Procore project can have thousands of users), so
   * the people popover asks `/api/item-assignees` — teammates first, then the
   * project's provider people — on open and on every query, debounced the
   * way `AssigneePicker` does it.
   */
  const [people, setPeople] = useState<ItemEditDirectoryOption[]>([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peopleFailed, setPeopleFailed] = useState(false);
  const peopleRequest = useRef(0);
  const directoryEnabled = item.assigneeDirectory !== false;
  const multipleAssignees = spec.shape === "people" && item.syncSource === "procore" &&
    (item.kind === "task" || item.kind === "punch" || item.category === "observations");
  const additionalAssignees = state.references.assignee?.additionalAssignees ?? item.additionalAssignees ?? [];
  const selectedAssigneeId = state.references.assignee?.remoteId ?? item.assigneeSuggestion?.selectedRemoteId ?? item.assigneeSuggestion?.selected?.remoteId;
  const canonicalPerson = (person: { remoteId: string; name: string }) => people.find(option => option.remoteId === person.remoteId || option.providerRemoteIds?.includes(person.remoteId)) ?? person;

  useEffect(() => {
    if (!open || spec.shape !== "people" || !directoryEnabled) return;
    const id = ++peopleRequest.current;
    setPeopleLoading(true);
    setPeopleFailed(false);
    const timer = window.setTimeout(() => {
      void fetchItemAssignees(item.jobSiteId, itemEditCategory(item), query.trim(), state.search, item.visitId)
        .then((next) => {
          if (peopleRequest.current === id) setPeople(next);
        })
        .catch(() => {
          if (peopleRequest.current === id) {
            setPeople([]);
            setPeopleFailed(true);
          }
        })
        .finally(() => {
          if (peopleRequest.current === id) setPeopleLoading(false);
        });
    }, 180);
    return () => {
      window.clearTimeout(timer);
      peopleRequest.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query, spec.shape, directoryEnabled, item.jobSiteId, item.visitId]);
  const [month, setMonth] = useState(() => {
    const at = parseDay(value) ?? today;
    return { m: at.getMonth(), y: at.getFullYear() };
  });

  // Every open starts clean: no stale filter, nothing highlighted, the tree
  // unfolded to the current value, the calendar on the current value's month.
  useEffect(() => {
    if (!open) return;
    setQuery(openQuery ?? "");
    setActive(-1);
    setKeyboard(false);
    setFoldedLocations(new Set());
    setFoldedInSearch(new Set());
    if ((field.reference && field.reference !== "assignee") || field.key === "type") state.loadOptions();
    const at = parseDay(value) ?? today;
    setMonth({ m: at.getMonth(), y: at.getFullYear() });
    if (spec.shape === "tree" && field.key === "type" && value) {
      const head = typeParts(value)[0];
      if (head) setExpanded({ [head]: true });
    }
    // A day of times opens on the one the field holds (or the working day).
    if (spec.shape === "time") {
      requestAnimationFrame(() => {
        const list = popRef.current?.querySelector<HTMLElement>(".rd-noscroll");
        const options = [...(list?.querySelectorAll<HTMLElement>('[data-testid="record-option"]') ?? [])];
        const target = options.find((node) => node.getAttribute("aria-selected") === "true" && !node.textContent?.startsWith("Not set"))
          ?? options.find((node) => node.textContent?.startsWith("7:00 AM"));
        if (list && target) list.scrollTop = target.offsetTop - list.clientHeight / 2 + target.clientHeight / 2;
      });
    }
    // Focus the popover itself when it has no search box, so arrow keys work.
    // A number opens on its own value, selected, so typing replaces it.
    if (spec.shape === "number") {
      requestAnimationFrame(() => {
        const input = popRef.current?.querySelector<HTMLInputElement>('[data-testid="record-number"]');
        input?.focus({ preventScroll: true });
        input?.select();
      });
    } else if (!spec.searchable) requestAnimationFrame(() => popRef.current?.focus({ preventScroll: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A number is typed, then saved ("Record Detail Web v2"): digits and one
  // decimal point only; Enter saves, Escape leaves it as it was.
  const [draft, setDraft] = useState("");
  useEffect(() => { if (open && spec.shape === "number") setDraft(value); }, [open]);
  const draftOk = /^\d*\.?\d*$/.test(draft) && draft !== ".";
  const draftRef = useRef({ draft, draftOk });
  draftRef.current = { draft, draftOk };

  // A click anywhere outside the property closes it — and keeps a number
  // typed into it, so a reviewer who types 7 and goes straight to Accept
  // accepts 7.
  useEffect(() => {
    if (!open || multipleAssignees) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) || popRef.current?.contains(target)) return;
      const typed = draftRef.current;
      if (spec.shape === "number" && typed.draftOk && typed.draft.replace(/\.$/, "") !== value) {
        commit(typed.draft.replace(/\.$/, ""));
        return;
      }
      onOpenChange(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, onOpenChange]);

  // The keyboard's row stays in sight (below any level pinned over it).
  useEffect(() => {
    if (!open || !keyboard) return;
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView?.({ block: "nearest" });
  }, [open, keyboard, active]);

  // The location tree opens on the place the record holds, once its
  // directory has arrived.
  const locationsShown = open && spec.shape === "tree" && field.reference === "location" ? state.directoryFor("location").length : 0;
  useLayoutEffect(() => {
    if (!open) {
      scrolledToLocation.current = false;
      return;
    }
    const list = listRef.current;
    if (!locationsShown || !list || scrolledToLocation.current) return;
    scrolledToLocation.current = true;
    const target = list.querySelector<HTMLElement>('[data-location-path][aria-selected="true"]');
    if (!target) return;
    const offset = target.getBoundingClientRect().top - list.getBoundingClientRect().top;
    list.scrollTop = Math.max(0, list.scrollTop + offset - list.clientHeight / 2 + target.clientHeight / 2);
  });

  // Over-the-page placement (`portal`): measured from the trigger, and again
  // whenever anything scrolls or the window resizes.
  const [placed, setPlaced] = useState<{ left: number; top?: number; bottom?: number; maxHeight: number } | null>(null);
  useLayoutEffect(() => {
    if (!open || !portal) {
      setPlaced(null);
      return;
    }
    const place = () => {
      const anchor = (triggerRef.current ?? containerRef.current)?.getBoundingClientRect();
      if (!anchor) return;
      const gutter = 12;
      const width = Math.min(spec.width, window.innerWidth - gutter * 2);
      const left = Math.max(gutter, Math.min(anchor.left + (popoverOffset?.left ?? 0), window.innerWidth - gutter - width));
      const below = window.innerHeight - anchor.bottom - gutter - 6;
      const above = anchor.top - gutter - 6;
      // What this editor wants at most: its list, its search box and padding.
      const wanted = (spec.shape === "people" ? 440 : spec.shape === "tree" ? 400 : spec.shape === "calendar" ? 330 : 320) + (spec.searchable ? 48 : 12);
      setPlaced(below >= Math.min(wanted, 260) || below >= above
        ? { left, top: anchor.bottom + 6, maxHeight: below }
        : { left, bottom: window.innerHeight - anchor.top + 6, maxHeight: above });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, portal, spec.width, spec.shape]);

  const refocusAfterClose = useRef(false);
  const close = (refocus: boolean) => {
    onOpenChange(false);
    refocusAfterClose.current = refocus;
    if (refocus) triggerRef.current?.focus();
  };
  // Resolving an ambiguous name replaces its trigger, in the same commit that
  // closes the editor. Focus the settled trigger from that commit: a commit made
  // after an await renders from a later task, so the next animation frame can
  // run first and focus the button that commit is about to remove.
  useLayoutEffect(() => {
    if (open || !refocusAfterClose.current) return;
    refocusAfterClose.current = false;
    triggerRef.current?.focus();
  }, [open]);
  const commit = (next: string, reference?: ItemEditDirectoryOption) => {
    peopleRequest.current += 1;
    state.update(field, next, reference);
    close(true);
    onCommit();
  };

  const selectSuggestedPerson = async (candidate: { remoteId: string; name: string }) => {
    const known = people.find((entry) => entry.remoteId === candidate.remoteId);
    if (known) {
      commit(known.name, known);
      return;
    }
    // A suggestion is an identity hint, not the full directory record. Resolve
    // its exact ID before applying company/trade metadata. Query changes,
    // closing the popover, or another selection invalidate this response.
    const request = ++peopleRequest.current;
    setPeopleLoading(true);
    setPeopleFailed(false);
    try {
      const directory = await fetchItemAssignees(item.jobSiteId, itemEditCategory(item), candidate.name, state.search, item.visitId);
      if (peopleRequest.current !== request) return;
      const resolved = directory.find((entry) => entry.remoteId === candidate.remoteId);
      if (!resolved) {
        setPeopleFailed(true);
        return;
      }
      setPeople(directory);
      commit(resolved.name, resolved);
    } catch {
      if (peopleRequest.current === request) setPeopleFailed(true);
    } finally {
      if (peopleRequest.current === request) setPeopleLoading(false);
    }
  };

  const q = query.trim().toLowerCase();
  const rows: Row[] = [];
  let emptyText = "";
  let sectionTop: React.ReactNode = null;
  let peopleCount = "";
  let candidateRows: Row[] = [];
  let unassignedRow: Row | null = null;

  if (open && spec.shape === "list") {
    if (field.choices) {
      const choices = field.choices.filter((choice) => !q || labelOf(choice).toLowerCase().includes(q));
      const markFor = (choice: string): React.ReactNode => {
        const host = optionMark?.(choice, labelOf(choice));
        if (host !== undefined) return host;
        if (field.key === "status") return <StatusMark look={lookFor(item, choice)} />;
        if (field.key === "priority") return <PriorityMark level={PRIORITY_LEVEL[valueKey(choice)] ?? 0} />;
        if (field.key === "taskCategory") return <Folder size={16} color={SUB} strokeWidth={2} aria-hidden />;
        return null;
      };
      // Clearing is offered only where the write path accepts an empty
      // value: a status and a punch priority always hold one.
      const clearable = field.key !== "status" && !(item.kind === "punch" && field.key === "priority");
      if (clearable && !q) {
        rows.push({
          id: "",
          kind: "option",
          label: "Not set",
          muted: true,
          mark: optionMark?.("", "") ?? (field.key === "priority" ? <PriorityMark level={0} /> : <StatusMark look={null} />),
          selected: !value,
          pick: () => commit(""),
        });
      }
      for (const choice of choices) {
        rows.push({
          id: choice,
          kind: "option",
          label: labelOf(choice),
          mark: markFor(choice),
          selected: valueKey(choice) === valueKey(value),
          pick: () => commit(choice),
        });
      }
      emptyText = "Nothing matches";
    } else {
      const directory = state.directoryFor(field.reference);
      const matches = directory.filter((option) => !q || option.name.toLowerCase().includes(q));
      if (!q) {
        rows.push({ id: "", kind: "option", label: "Not set", muted: true, mark: <StatusMark look={null} />, selected: !value, pick: () => commit("") });
      }
      for (const option of matches) {
        rows.push({
          id: option.remoteId,
          kind: "option",
          label: option.name,
          mark: field.reference === "trade" ? <TradeMark value={option.name} /> : <Building2 size={16} color={SUB} strokeWidth={2} aria-hidden />,
          selected:
            state.references[field.reference!]?.remoteId === option.remoteId ||
            (!state.references[field.reference!] && valueKey(option.name) === valueKey(value)),
          pick: () => commit(option.name, option),
        });
      }
      const acceptsTypedValue = field.reference !== "trade" || item.assigneeDirectory === false;
      if (acceptsTypedValue && q && !matches.some((option) => option.name.trim().toLowerCase() === q)) {
        rows.push({
          id: `typed:${q}`,
          kind: "option",
          label: `Use “${query.trim()}”`,
          mark: <Tag size={16} color={SUB} strokeWidth={2} aria-hidden />,
          pick: () => commit(query.trim()),
        });
      }
      emptyText = state.optionsLoading
        ? "Loading…"
        : state.optionsFailed
          ? state.optionsError ?? "The project directory could not be loaded."
          : q
            ? `No ${field.label.toLowerCase()} matches “${query.trim()}”`
            : `This project has no ${field.label.toLowerCase()} list yet.`;
    }
  }

  if (open && spec.shape === "tree" && field.key === "type") {
    // The project's own Procore types once loaded; Procore's defaults until
    // then, or when the project has none — the phone's same fallback.
    const all: readonly string[] =
      state.observationTypes.length > 0 ? state.observationTypes : DEFAULT_PROCORE_OBSERVATION_TYPE_OPTIONS;
    const families = new Map<string, string[]>();
    for (const type of all) {
      const [family] = typeParts(type);
      families.set(family, [...(families.get(family) ?? []), type]);
    }
    const isCurrent = (type: string) => valueKey(type) === valueKey(value);
    for (const [family, members] of families) {
      const solo = members.length === 1 && typeParts(members[0]!)[1] === family;
      if (q) {
        for (const type of members) {
          const name = typeParts(type)[1];
          if (!(name.toLowerCase().includes(q) || family.toLowerCase().includes(q))) continue;
          rows.push({ id: type, kind: "leaf", label: name, sub: solo ? undefined : family, mark: <ObservationTypeMark value={type} />, selected: isCurrent(type), pick: () => commit(type) });
        }
        continue;
      }
      if (solo) {
        const type = members[0]!;
        rows.push({ id: type, kind: "leaf", label: family, mark: <ObservationTypeMark value={type} />, selected: isCurrent(type), pick: () => commit(type) });
        continue;
      }
      const isOpen = !!expanded[family];
      const toggle = (next: boolean) => setExpanded((prev) => ({ ...prev, [family]: next }));
      rows.push({
        id: `family:${family}`,
        kind: "branch",
        label: family,
        count: members.length,
        expanded: isOpen,
        holdsValue: members.some(isCurrent),
        pick: () => toggle(!isOpen),
        expand: toggle,
      });
      if (isOpen) {
        members.forEach((type, index) => {
          rows.push({
            id: type,
            kind: "leaf",
            label: typeParts(type)[1],
            mark: <ObservationTypeMark value={type} />,
            nested: true,
            lastNested: index === members.length - 1,
            selected: isCurrent(type),
            pick: () => commit(type),
          });
        });
      }
    }
    emptyText = "No matching types";
  }

  // The phone's location tree (`LocationTreePicker` over `locationTree.ts`):
  // the provider's own hierarchy — Procore's `A>B>C` names, ACC's parent ids —
  // every level open, and a search that keeps each match's ancestors.
  let locationLayout: LocationRow[] = [];
  let locationStart = 0;
  if (open && spec.shape === "tree" && field.reference === "location") {
    const directory = state.directoryFor("location");
    const byRemote = new Map(directory.map((option) => [option.remoteId, option]));
    const tree = buildLocationTree(directory.map((option) => ({
      remoteId: option.remoteId,
      name: option.name,
      ...(option.path ? { path: option.path } : {}),
      ...(option.parentRemoteId ? { parentRemoteId: option.parentRemoteId } : {}),
    })));
    const searching = !!q;
    const folded = searching ? foldedInSearch : foldedLocations;
    const fold = (id: string, open: boolean) =>
      (searching ? setFoldedInSearch : setFoldedLocations)((prev) => {
        const next = new Set(prev);
        if (open) next.delete(id);
        else next.add(id);
        return next;
      });
    // The value's place: by the reference the record holds, else by a name
    // only one place carries (two rooms called "Kitchen" identify neither).
    const selectedRemote = state.references.location?.remoteId;
    let selectedNode: string | null = selectedRemote && byRemote.has(selectedRemote) ? `id:${selectedRemote}` : null;
    if (!selectedNode && value) {
      const wanted = normalizeLocationQuery(value);
      const matches: string[] = [];
      const visit = (nodes: LocationNode[]) => {
        for (const node of nodes) {
          if (node.option && (normalizeLocationQuery(node.option.name) === wanted || normalizeLocationQuery(node.path) === wanted)) matches.push(node.id);
          visit(node.children);
        }
      };
      visit(tree);
      selectedNode = matches.length === 1 ? matches[0]! : null;
    }
    const holds = (node: LocationNode): boolean => node.children.some((child) => child.id === selectedNode || holds(child));

    if (!q && spec.clearable !== false) {
      rows.push({ id: "", kind: "leaf", label: "No location", mark: <MapPin size={15} color={SUB} strokeWidth={2} style={DASHED} aria-hidden />, selected: !value, pick: () => commit("") });
    }
    locationStart = rows.length;
    locationLayout = visibleLocationRows(tree, query, folded);
    for (const layout of locationLayout) {
      const { node } = layout;
      const branch = node.children.length > 0;
      const unfolded = !folded.has(node.id);
      const option = node.option ? byRemote.get(node.option.remoteId) : undefined;
      rows.push({
        id: node.id,
        kind: "location",
        label: node.label,
        sub: node.path.replace(/ > /g, " › "),
        depth: layout.depth,
        branch,
        expanded: branch ? unfolded : undefined,
        selected: node.id === selectedNode,
        holdsValue: branch && !unfolded && holds(node),
        pick: () => (option ? commit(option.name, option) : fold(node.id, !unfolded)),
        expand: branch ? (next) => fold(node.id, next) : undefined,
      });
    }
    if (q && !directory.some((option) => normalizeLocationQuery(option.name) === normalizeLocationQuery(query))) {
      rows.push({
        id: `typed:${q}`,
        kind: "leaf",
        label: `Use “${query.trim()}”`,
        mark: <MapPin size={15} color={SUB} strokeWidth={2} aria-hidden />,
        pick: () => commit(query.trim()),
      });
    }
    emptyText = state.optionsLoading
      ? "Loading locations…"
      : state.optionsFailed
        ? state.optionsError ?? "Project locations could not be loaded."
        : q
          ? "No matching locations"
          : "This project has no locations yet.";
  }

  if (open && spec.shape === "people") {
    const directory = people;
    const selectedId = state.references.assignee?.remoteId;
    const isCurrent = (remoteId: string, name: string) =>
      selectedId ? selectedId === remoteId : !!value && valueKey(name) === valueKey(value);
    if (!q) {
      unassignedRow = { id: "", kind: "unassigned", label: "Unassigned", selected: !value, pick: () => commit("") };
      const candidates = (item.assigneeSuggestion?.candidates ?? []).filter((candidate) => candidate.confidencePercent !== undefined).slice(0, 3);
      candidateRows = candidates.map((candidate, index) => {
        return {
          id: `candidate:${candidate.remoteId}`,
          kind: "candidate",
          label: candidate.name,
          sub: [candidate.jobTitle, candidate.company].filter(Boolean).join(" · ") || undefined,
          pct: Math.round(candidate.confidencePercent!),
          top: index === 0,
          face: personMark?.(candidate.name),
          selected: isCurrent(candidate.remoteId, candidate.name),
          pick: () => { void selectSuggestedPerson(candidate); },
        };
      });
      if (candidateRows.length > 0) {
        sectionTop = (
          <SectionLabel
            left={
              <>
                <Sparkles size={13} color={GREEN} strokeWidth={2} aria-hidden />
                <span>{candidateRows.length === 1 ? "1 likely assignee" : `${candidateRows.length} likely assignees`}</span>
              </>
            }
          />
        );
      }
    }
    // The server already matched the query; this only narrows a stale page
    // while the next one is in flight.
    const matches = directory.filter((option) =>
      !q || [option.name, option.email, option.company].some((part) => part?.toLowerCase().includes(q)),
    );
    for (const option of matches) {
      rows.push({
        id: option.remoteId,
        kind: "person",
        label: option.name,
        face: personMark?.(option.name),
        sub: [option.company, option.email].filter(Boolean).join(" · ") || undefined,
        selected: isCurrent(option.remoteId, option.name),
        pick: () => commit(option.name, option),
      });
    }
    // A project with no linked directory takes the name as typed — the
    // phone's own free-text assignee.
    if (!directoryEnabled && q && !matches.some((option) => option.name.trim().toLowerCase() === q)) {
      rows.push({
        id: `typed:${q}`,
        kind: "option",
        label: `Assign “${query.trim()}”`,
        mark: <User size={16} color={SUB} strokeWidth={2} aria-hidden />,
        pick: () => commit(query.trim(), { remoteId: "", name: query.trim() }),
      });
    }
    peopleCount = peopleLoading
      ? "Searching…"
      : q
        ? `${matches.length} match${matches.length === 1 ? "" : "es"}`
        : `${directory.length} ${directory.length === 1 ? "person" : "people"}`;
    emptyText = !directoryEnabled
      ? "Type a name to assign it."
      : peopleLoading
      ? "Loading the project directory…"
      : peopleFailed
        ? "The project directory could not be loaded."
        : q
          ? `No directory matches for “${query.trim()}”.`
          : "This project has no directory yet.";
  }

  if (open && spec.shape === "time") {
    const typed = q ? parseTypedTime(query) : "";
    const clock = <Clock size={15} color={SUB} strokeWidth={2} aria-hidden />;
    const stored = parseStoredTime(value);
    const current = stored ? `${String(stored.hours).padStart(2, "0")}:${String(stored.minutes).padStart(2, "0")}` : "";
    if (!q) {
      rows.push({ id: "", kind: "option", label: "Not set", muted: true, mark: <Clock size={15} color={SUB} strokeWidth={2} style={DASHED} aria-hidden />, selected: !value, pick: () => commit("") });
    }
    if (typed && !TIME_STEPS.includes(typed)) {
      rows.push({ id: `typed:${typed}`, kind: "option", label: `Use ${displayTime(typed)}`, mark: clock, pick: () => commit(typed) });
    }
    const compactQuery = q.replace(/\s+/g, "");
    for (const step of TIME_STEPS) {
      const label = displayTime(step) ?? step;
      if (compactQuery && !(label.toLowerCase().replace(/\s+/g, "").includes(compactQuery) || step === typed)) continue;
      rows.push({ id: step, kind: "option", label, mark: clock, selected: current === step, pick: () => commit(step) });
    }
    emptyText = typed === null ? "That is not a time. Try 7:30 AM." : "No matching times";
  }

  // The keyboard walks every row in reading order: Unassigned, the likely
  // assignees, then the directory.
  const navigable: Row[] = [...(unassignedRow ? [unassignedRow] : []), ...candidateRows, ...rows];

  const onKeyDown = (event: React.KeyboardEvent) => {
    const n = navigable.length;
    const go = (index: number) => {
      setActive(index);
      setKeyboard(true);
    };
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (n) go(Math.min(n - 1, active + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (n) go(Math.max(0, active - 1));
    } else if (event.key === "Home" && !spec.searchable) {
      event.preventDefault();
      if (n) go(0);
    } else if (event.key === "End" && !spec.searchable) {
      event.preventDefault();
      if (n) go(n - 1);
    } else if (event.key === "Enter") {
      const row = navigable[active];
      if (row) {
        event.preventDefault();
        row.pick();
      }
    } else if ((event.key === "ArrowRight" || event.key === "ArrowLeft") && navigable[active]?.expand) {
      event.preventDefault();
      navigable[active]!.expand!(event.key === "ArrowRight");
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close(true);
    }
  };

  const hover = (index: number) => () => {
    if (active !== index || keyboard) {
      setActive(index);
      setKeyboard(false);
    }
  };

  // ----- the trigger's face -----
  let mark: React.ReactNode;
  let shown = value;
  let fg = INK;
  let full = value;
  if (field.inputType === "date") {
    const day = parseDay(value);
    const late = !!day && day < today;
    const soon = !!day && !late && (day.getTime() - today.getTime()) / 864e5 <= 7;
    shown = fmtDay(day);
    full = shown;
    fg = late ? RED : soon ? TONE.orange : INK;
    mark = <CalendarIcon size={15} color={late ? RED : soon ? TONE.orange : SUB} strokeWidth={2} style={day ? undefined : DASHED} aria-hidden />;
  } else if (field.key === "status") {
    shown = optionLabel(item, field, value);
    mark = <StatusMark look={value ? lookFor(item, value) : null} size={15} />;
  } else if (field.key === "priority") {
    shown = value ? optionLabel(item, field, value) : "";
    mark = <PriorityMark level={PRIORITY_LEVEL[valueKey(value)] ?? 0} size={15} />;
  } else if (field.key === "taskCategory") {
    mark = <Folder size={15} color={SUB} strokeWidth={2} style={value ? undefined : DASHED} aria-hidden />;
  } else if (field.key === "type") {
    shown = value ? typeLabel(value) : "";
    mark = value ? <ObservationTypeMark value={value} /> : <Flag size={15} color={SUB} strokeWidth={2} style={DASHED} aria-hidden />;
  } else if (field.reference === "assignee") {
    if (additionalAssignees.length) { shown = `${value} +${additionalAssignees.length}`; full = [value, ...additionalAssignees.map(person => person.name)].join(", "); }
    mark = value ? <Avatar name={value} size={16} /> : <User size={15} color={SUB} strokeWidth={2} style={DASHED} aria-hidden />;
  } else if (field.reference === "trade") {
    mark = value ? <TradeMark value={value} /> : <Tag size={15} color={SUB} strokeWidth={2} style={DASHED} aria-hidden />;
  } else if (field.reference === "location") {
    shown = locationParts(issuePath(item, value)).join(" › ");
    full = shown;
    mark = <MapPin size={15} color={value ? TONE.info : SUB} strokeWidth={2} style={value ? undefined : DASHED} aria-hidden />;
  } else {
    mark = <Building2 size={15} color={SUB} strokeWidth={2} style={value ? undefined : DASHED} aria-hidden />;
  }
  if (field.inputType === "time") {
    shown = value ? displayTime(value) ?? value : "";
    full = shown;
  } else if (field.choices && value && !["status", "priority", "type"].includes(field.key)) {
    shown = labelOf(value);
    full = shown;
  }
  const isSet = !!shown;

  // ----- calendar -----
  const calendar = () => {
    const first = new Date(month.y, month.m, 1);
    const days = new Date(month.y, month.m + 1, 0).getDate();
    const pad = (first.getDay() + 6) % 7;
    const current = parseDay(value);
    const cells: React.ReactNode[] = [];
    for (let i = 0; i < pad; i++) cells.push(<span key={`b${i}`} />);
    for (let d = 1; d <= days; d++) {
      const date = new Date(month.y, month.m, d);
      const selected = sameDay(date, current);
      const isToday = sameDay(date, today);
      cells.push(
        <button
          key={d}
          type="button"
          className="rd-cal-day"
          data-testid="record-calendar-day"
          aria-pressed={selected}
          aria-label={fmtDay(date)}
          onClick={() => commit(isoDay(date))}
          style={{
            height: 32,
            borderRadius: "50%",
            border: 0,
            background: selected ? INK : "transparent",
            color: selected ? CARD : INK,
            boxShadow: isToday && !selected ? `inset 0 0 0 1px ${TONE.amber}` : "none",
            fontSize: 13,
            fontWeight: selected ? 700 : 500,
            fontVariantNumeric: "tabular-nums",
            cursor: "pointer",
            fontFamily: "inherit",
            padding: 0,
          }}
        >
          {d}
        </button>,
      );
    }
    const step = (delta: number) =>
      setMonth(({ m, y }) => {
        const next = m + delta;
        return next < 0 ? { m: 11, y: y - 1 } : next > 11 ? { m: 0, y: y + 1 } : { m: next, y };
      });
    const navButton = (delta: number, label: string, icon: React.ReactNode) => (
      <button
        type="button"
        className="rd-cal-nav"
        aria-label={label}
        onClick={() => step(delta)}
        style={{ width: 30, height: 30, borderRadius: "50%", border: 0, background: "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0 }}
      >
        {icon}
      </button>
    );
    const footButton = (label: string, color: string, onClick: () => void) => (
      <button
        type="button"
        className="rd-btn"
        onClick={onClick}
        style={{ flex: 1, height: 32, borderRadius: 8, border: `1px solid ${LINE}`, background: CARD, fontSize: 12.5, fontWeight: 600, color, cursor: "pointer", fontFamily: "inherit" }}
      >
        {label}
      </button>
    );
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "4px 4px 2px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {navButton(-1, "Previous month", <ChevronLeft size={16} color={INK} strokeWidth={2} aria-hidden />)}
          <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: -0.2 }}>{`${MONTHS[month.m]} ${month.y}`}</span>
          {navButton(1, "Next month", <ChevronRight size={16} color={INK} strokeWidth={2} aria-hidden />)}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3 }}>
          {["M", "T", "W", "T", "F", "S", "S"].map((letter, index) => (
            <span key={index} style={{ height: 22, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: MONO, fontSize: 9.5, fontWeight: 600, letterSpacing: 0.8, color: TER }}>
              {letter}
            </span>
          ))}
          {cells}
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          {footButton("Today", INK, () => commit(isoDay(today)))}
          {footButton("Not set", SUB, () => commit(""))}
        </div>
      </div>
    );
  };

  const rowNodes = (list: Row[], offset: number) =>
    list.map((row, index) => (
      <PopoverRow key={`${row.kind}:${row.id}`} row={row} active={active === offset + index} keyboard={keyboard} onHover={hover(offset + index)} />
    ));
  // A level pins under the ones above it while its places scroll past — the
  // phone's sticky ancestors, here by `position: sticky` inside each level's
  // own group, so a level lets go exactly when its last place leaves.
  const locationNodes = (from: number, to: number): React.ReactNode[] => {
    const out: React.ReactNode[] = [];
    for (let at = from; at < to; ) {
      const layout = locationLayout[at]!;
      const index = rowOffset + locationStart + at;
      const node = <PopoverRow key={`location:${layout.node.id}`} row={rows[locationStart + at]!} active={active === index} keyboard={keyboard} onHover={hover(index)} />;
      if (layout.end > at + 1) {
        const pins = layout.depth < LOCATION_PINNED_LEVELS;
        out.push(
          <div key={`level:${layout.node.id}`} role="group" style={{ display: "flex", flexDirection: "column" }}>
            <div
              data-location-pin={pins || undefined}
              style={pins ? { position: "sticky", top: layout.depth * LOCATION_ROW, zIndex: LOCATION_PINNED_LEVELS - layout.depth, background: CARD } : undefined}
            >
              {node}
            </div>
            {locationNodes(at + 1, layout.end)}
          </div>,
        );
        at = layout.end;
      } else {
        out.push(node);
        at += 1;
      }
    }
    return out;
  };
  // A pinned level reads as lifted only while something scrolls under it,
  // and only the lowest one casts the shadow.
  const markPinned = (list: HTMLElement) => {
    const pins = [...list.querySelectorAll<HTMLElement>("[data-location-pin]")];
    const lifted = pins.filter((pin) => pin.getBoundingClientRect().top - (pin.parentElement?.getBoundingClientRect().top ?? 0) > 0.5);
    for (const pin of pins) {
      if (lifted.includes(pin)) pin.setAttribute("data-pinned", pin === lifted.at(-1) ? "edge" : "");
      else pin.removeAttribute("data-pinned");
    }
  };
  const unassignedOffset = 0;
  const candidateOffset = unassignedRow ? 1 : 0;
  const rowOffset = candidateOffset + candidateRows.length;
  const locationList = locationLayout.length > 0 && (
    <>
      {rowNodes(rows.slice(0, locationStart), rowOffset)}
      {locationNodes(0, locationLayout.length)}
      {rowNodes(rows.slice(locationStart + locationLayout.length), rowOffset + locationStart + locationLayout.length)}
    </>
  );

  const maybePortal = (node: React.ReactElement) =>
    portal && typeof document !== "undefined" ? createPortal(node, document.body) : node;

  const parts = locationParts(value);
  const compactTrigger = (
    <button
      ref={triggerRef}
      type="button"
      className="rd-trig"
      data-testid="record-property-trigger"
      aria-haspopup="tree"
      aria-expanded={open}
      aria-label={`${field.label}: ${isSet ? shown : "not set"}`}
      title={full || field.label}
      onClick={() => onOpenChange(!open)}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, maxWidth: "100%", height: 26, padding: "0 8px 0 6px", marginLeft: -6, borderRadius: 6, border: 0, outline: "none", background: open ? SEC : "transparent", fontSize: 12.5, fontWeight: 500, color: isSet ? SUB : TER, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}
    >
      <MapPin size={13} color={isSet ? TONE.info : TER} strokeWidth={2} style={isSet ? undefined : DASHED} aria-hidden />
      <span style={{ flex: "none", color: isSet ? INK : TER }}>{isSet ? parts[parts.length - 1] : spec.unsetCopy}</span>
      {parts.length > 1 && (
        <span style={{ flex: "0 1 auto", minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: QUIET }}>{`· ${parts.slice(0, -1).join(" › ")}`}</span>
      )}
    </button>
  );

  return (
    <div ref={containerRef} data-testid="record-property" data-property={field.key} style={{ position: "relative", zIndex: open ? 20 : 1, ...(renderTrigger ? { display: "inline-flex", maxWidth: "100%" } : {}) }}>
      {renderTrigger ? renderTrigger({ open, toggle: () => onOpenChange(!open), triggerRef, shown, full, isSet, unsetCopy: spec.unsetCopy }) : compact ? compactTrigger : (
      <div className="rd-row" style={{ display: "flex", alignItems: "center", gap: 4, margin: "0 -8px" }}>
        <button
          ref={triggerRef}
          type="button"
          className="rd-trig"
          data-testid="record-property-trigger"
          aria-haspopup={spec.shape === "calendar" ? "dialog" : spec.shape === "tree" ? "tree" : "listbox"}
          aria-expanded={open}
          aria-label={`${field.label}: ${isSet ? shown : "not set"}`}
          title={full || field.label}
          onClick={() => onOpenChange(!open)}
          style={{
            flex: "0 1 auto",
            minWidth: 0,
            maxWidth: "100%",
            display: "flex",
            alignItems: "center",
            gap: 9,
            height: 32,
            padding: "0 10px 0 8px",
            borderRadius: 8,
            border: 0,
            outline: "none",
            background: open ? SEC : "transparent",
            fontSize: 13.5,
            fontWeight: 500,
            color: isSet ? fg : SUB,
            cursor: "pointer",
            textAlign: "left",
            fontFamily: "inherit",
          }}
        >
          <span style={{ width: 16, display: "inline-flex", justifyContent: "center", flex: "none" }}>{mark}</span>
          <span style={{ minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{isSet ? shown : spec.unsetCopy}</span>
        </button>
        <CopyButton label={field.label.toLowerCase()} done={copied === field.key} hasValue={isSet} restVisible onCopy={() => onCopy(field.key, full || shown)} />
      </div>
      )}

      {open && multipleAssignees && <AssignToModal
        label={field.label}
        typeLabel={item.kind === "log" ? "Observation" : item.kind === "punch" ? "Punch" : "Task"}
        value={value}
        committedRemoteId={selectedAssigneeId ? canonicalPerson({ remoteId: selectedAssigneeId, name: value }).remoteId : undefined}
        suggestion={preferWorkspaceSuggestion(item.assigneeSuggestion, people)}
        query={query} onQueryChange={setQuery} options={people} isLoading={peopleLoading} failed={peopleFailed}
        multiple={{ selected: [
          ...(selectedAssigneeId && value ? [canonicalPerson({ remoteId: selectedAssigneeId, name: value })] : []),
          ...additionalAssignees.map(canonicalPerson),
        ].filter((person, index, all) => all.findIndex(other => other.remoteId === person.remoteId) === index) }}
        onCommit={async (choice, selected) => {
          let primary = choice ? canonicalPerson(choice) : null;
          // Ranked suggestions carry identity hints, not trusted company/trade metadata.
          // Resolve the exact identity before saving, just like the single-person editor.
          if (primary && !people.some(person => person.remoteId === primary!.remoteId || person.providerRemoteIds?.includes(primary!.remoteId))) {
            const request = ++peopleRequest.current;
            setPeopleLoading(true); setPeopleFailed(false);
            try {
              const directory = await fetchItemAssignees(item.jobSiteId, itemEditCategory(item), primary.name, state.search, item.visitId);
              if (peopleRequest.current !== request) return false;
              const resolved = directory.find(person => person.remoteId === primary!.remoteId || person.providerRemoteIds?.includes(primary!.remoteId));
              if (!resolved) { setPeopleFailed(true); return false; }
              primary = resolved;
            } catch { if (peopleRequest.current === request) setPeopleFailed(true); return false; }
            finally { if (peopleRequest.current === request) setPeopleLoading(false); }
          }
          const extras = (selected ?? []).slice(1).map(canonicalPerson)
            .filter((person, index, all) => person.remoteId !== primary?.remoteId && all.findIndex(other => other.remoteId === person.remoteId) === index)
            .map(({ remoteId, name }) => ({ remoteId, name }));
          commit(primary?.name ?? "", { ...(primary ?? { remoteId: "", name: "" }), additionalAssignees: extras });
        }}
        onClose={() => close(true)} testId="record-assignees"
      />}
      {open && !multipleAssignees && (!portal || placed) && maybePortal(
        <div
          ref={popRef}
          role={spec.shape === "calendar" || spec.shape === "number" ? "dialog" : spec.shape === "tree" ? "tree" : "listbox"}
          aria-label={field.label}
          tabIndex={-1}
          data-testid="record-popover"
          onKeyDown={onKeyDown}
          style={{
            ...(portal && placed
              ? {
                  position: "fixed",
                  left: placed.left,
                  ...(placed.top !== undefined ? { top: placed.top } : { bottom: placed.bottom }),
                  maxHeight: placed.maxHeight,
                  overflow: "hidden",
                  zIndex: 1000,
                }
              : {
                  position: "absolute",
                  left: popoverOffset?.left ?? (compact ? -6 : -8),
                  top: popoverOffset?.top ?? (compact ? 30 : 34),
                  zIndex: 500,
                }),
            width: spec.width,
            maxWidth: "calc(100vw - 32px)",
            background: CARD,
            border: `1px solid ${LINE2}`,
            borderRadius: 12,
            boxShadow: "0 14px 30px rgba(22,18,10,.2)",
            padding: 6,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            outline: "none",
            animation: "rdPopIn .14s ease-out both",
          }}
        >
          {spec.searchable && (
            <SearchBox
              value={query}
              placeholder={spec.placeholder}
              hotkey={spec.hotkey}
              onChange={(next) => {
                setQuery(next);
                setFoldedInSearch(new Set());
                setActive(next ? 0 : -1);
                setKeyboard(false);
              }}
            />
          )}
          {spec.shape === "number" ? (
            <div style={{ padding: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, height: 36, padding: "0 10px", borderRadius: 8, background: CARD, boxShadow: `0 0 0 1px ${draftOk ? INK : RD.red}` }}>
                <input
                  autoFocus
                  data-testid="record-number"
                  type="text"
                  inputMode="decimal"
                  {...NO_AUTOFILL}
                  value={draft}
                  placeholder={field.label}
                  aria-label={field.label}
                  aria-invalid={!draftOk || undefined}
                  onChange={(event) => setDraft(event.target.value)}
                  onInput={(event) => setDraft((event.target as HTMLInputElement).value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      event.stopPropagation();
                      if (draftOk) commit(draft.replace(/\.$/, ""));
                    } else if (event.key === "Escape") {
                      event.preventDefault();
                      event.stopPropagation();
                      close(true);
                    }
                  }}
                  style={{ flex: 1, minWidth: 0, border: 0, outline: "none", background: "transparent", fontSize: 14, color: INK, padding: 0, fontFamily: "inherit", fontVariantNumeric: "tabular-nums" }}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, minHeight: 18 }}>
                <span data-testid="record-number-hint" style={{ fontSize: 11.5, color: draftOk ? QUIET : RD.redInk }}>
                  {draftOk ? (draft === "" ? "Leave empty to clear" : "Numbers only") : "Enter a number, e.g. 6"}
                </span>
                <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 600, letterSpacing: 1, color: QUIET }}>↵ SAVE</span>
              </div>
            </div>
          ) : spec.shape === "calendar" ? (
            calendar()
          ) : (
            <div
              ref={listRef}
              className="rd-noscroll"
              data-testid="record-option-list"
              onScroll={locationLayout.length ? (event) => markPinned(event.currentTarget) : undefined}
              onMouseLeave={() => {
                if (!keyboard) setActive(-1);
              }}
              style={{ maxHeight: spec.shape === "people" ? 440 : spec.shape === "tree" ? 400 : 320, minHeight: 0, flex: "0 1 auto", overflow: "auto", display: "flex", flexDirection: "column" }}
            >
              {unassignedRow && rowNodes([unassignedRow], unassignedOffset)}
              {sectionTop}
              {rowNodes(candidateRows, candidateOffset)}
              {spec.shape === "people" && <SectionLabel left={q ? "Search people" : "Everyone"} right={peopleCount} />}
              {locationList || rowNodes(rows, rowOffset)}
              {/* Said even under a lone "Not set" / "No location": a directory
                  that is loading, failed or empty must not read as a list. */}
              {rows.every((row) => row.id === "" || row.id.startsWith("typed:")) && emptyText && (
                <Empty
                  text={emptyText}
                  onClear={q && spec.shape === "tree" ? () => setQuery("") : undefined}
                />
              )}
            </div>
          )}
        </div>,
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Photos: one focused photo, its description and location, and a filmstrip
// ---------------------------------------------------------------------------

/** What the record page can do to its photos. Each is optional: a write the
 *  caller cannot make leaves its control out rather than showing a dead one. */
export interface RecordPhotoActions {
  /** Open the attach-or-upload picker. */
  onAdd?: () => void;
  /** Take the photo off this record (the walk keeps it). */
  onRemove?: (photoId: string) => void;
  /** The description everyone sees — the phone's own caption edit. */
  onDescription?: (photoId: string, description: string) => void;
  /** Where this one photo was taken. */
  onLocation?: (photoId: string, name: string, reference?: ItemEditDirectoryOption) => void;
  busy?: boolean;
  error?: string | null;
}

function PhotoCarousel({
  evidence,
  focus,
  onFocus,
  noun,
  actions,
  state,
  item,
  today,
  openKey,
  setOpenKey,
  copied,
  onCopy,
  onOpenPhoto,
}: {
  evidence: ItemEvidenceRow[];
  focus: number;
  onFocus: (index: number) => void;
  noun: string;
  actions: RecordPhotoActions;
  state: PropertyState;
  item: ConsoleRow;
  today: Date;
  openKey: string | null;
  setOpenKey: (key: string | null) => void;
  copied: string | null;
  onCopy: (key: string, text: string) => void;
  onOpenPhoto: (index: number) => void;
}) {
  const n = evidence.length;
  const photo = evidence[focus];
  const saved = photo?.description ?? "";
  const [draft, setDraft] = useState(saved);
  // A new focused photo, or a refreshed description, replaces the draft.
  useEffect(() => setDraft(saved), [photo?.id, saved]);

  if (!photo) {
    return (
      <div data-testid="record-no-photos" style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderRadius: 10, border: `1.5px dashed ${LINE2}`, color: TER, fontSize: 13.5, fontWeight: 500 }}>
        <span style={{ flex: 1 }}>{`No photos on this ${noun}.`}</span>
        {actions.onAdd && (
          <button type="button" className="rd-btn" data-testid="record-photo-add" disabled={actions.busy} onClick={actions.onAdd} style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 28, padding: "0 10px", borderRadius: 7, border: `1px solid ${LINE}`, background: "transparent", fontSize: 12.5, fontWeight: 600, color: INK, cursor: "pointer", fontFamily: "inherit" }}>
            <Plus size={14} color={INK} strokeWidth={2} aria-hidden />
            Add photo
          </button>
        )}
      </div>
    );
  }

  const big = photo.url ?? photo.thumbnailUrl;
  const step = (delta: number) => onFocus((focus + delta + n) % n);
  const badge: React.CSSProperties = { position: "absolute", fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: 0.9, color: RD.white, background: "rgba(10,10,10,.6)", padding: "4px 7px", borderRadius: 5, pointerEvents: "none" };
  const round = (side: "left" | "right"): React.CSSProperties => ({ position: "absolute", [side]: 12, top: "50%", transform: "translateY(-50%)", width: 32, height: 32, borderRadius: 16, border: 0, background: "rgba(255,253,248,.92)", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 4px 12px rgba(10,10,10,.18)", padding: 0 });

  // The photo's location, through the same tree the record's Location uses.
  const locationKey = `photo:${photo.id}`;
  const locationValue = photo.location ? photo.location.name : "";
  const locationField: ReviewField = { key: "photoLocation", label: "Photo location", value: locationValue, rawValue: locationValue, reference: "location" };
  const locationSpec: PropertySpec = { ...specFor(locationField), placeholder: "Change photo location…", hotkey: undefined, clearable: false, unsetCopy: "Add location" };
  const locationState: PropertyState = {
    ...state,
    values: { photoLocation: locationValue },
    references: photo.location?.procoreLocationId ? { location: { remoteId: photo.location.procoreLocationId, name: photo.location.name } } : {},
    update: (_field, next, reference) => {
      if (next.trim()) actions.onLocation?.(photo.id, next.trim(), reference);
    },
  };

  const commitDescription = () => {
    const next = draft.trim();
    if (!next) {
      // The write path takes no empty description; put the old one back.
      setDraft(saved);
      return;
    }
    if (next !== saved.trim()) actions.onDescription?.(photo.id, next);
  };

  return (
    <div data-testid="record-photos" style={{ maxWidth: 460, opacity: actions.busy ? 0.6 : 1, transition: "opacity .15s" }}>
      <div className="rd-photo" style={{ position: "relative", width: "100%", aspectRatio: "3 / 2", borderRadius: 12, overflow: "hidden", background: RD.photoGround }}>
        <button
          type="button"
          data-testid="evidence-item"
          onClick={() => onOpenPhoto(focus)}
          aria-label={saved ? `Open photo: ${saved}` : "Open photo"}
          style={{ position: "absolute", inset: 0, border: 0, padding: 0, background: "transparent", cursor: "zoom-in", display: "block", width: "100%", height: "100%" }}
        >
          {big ? (
            <PhotoImage className="rd-photo-img" src={big} alt={saved} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "filter .12s" }} />
          ) : (
            <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: MONO, fontSize: 9, letterSpacing: 0.8, color: TER, textTransform: "uppercase" }}>
              Photo unavailable
            </span>
          )}
        </button>
        {photo.capturedAt && <span style={{ ...badge, left: 12, top: 12 }}>{whenStamp(photo.capturedAt)}</span>}
        <span data-testid="record-photo-count" style={{ ...badge, right: 12, bottom: 12 }}>{`${focus + 1} / ${n}`}</span>
        {actions.onRemove && (
          <button
            type="button"
            className="rd-detach"
            data-testid="record-photo-remove"
            aria-label="Remove photo from this record"
            title="Remove"
            disabled={actions.busy}
            onClick={() => actions.onRemove!(photo.id)}
            style={{ position: "absolute", right: 12, top: 12, width: 28, height: 28, borderRadius: 14, border: 0, background: "rgba(10,10,10,.6)", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0, transition: "background .12s" }}
          >
            <MinusCircle size={16} color={RD.white} strokeWidth={2} aria-hidden />
          </button>
        )}
        {n > 1 && (
          <>
            <button type="button" className="rd-step" data-testid="record-photo-prev" aria-label="Previous photo" onClick={() => step(-1)} style={round("left")}>
              <ChevronLeft size={16} color={INK} strokeWidth={2.2} aria-hidden />
            </button>
            <button type="button" className="rd-step" data-testid="record-photo-next" aria-label="Next photo" onClick={() => step(1)} style={round("right")}>
              <ChevronRight size={16} color={INK} strokeWidth={2.2} aria-hidden />
            </button>
          </>
        )}
      </div>

      <div style={{ marginTop: 10 }}>
        <div className="rd-row" style={{ position: "relative", paddingRight: 26 }}>
          {actions.onDescription ? (
            <textarea
              className="rd-desc"
              data-testid="record-photo-description"
              aria-label="Photo description"
              rows={1}
              value={draft}
              placeholder="Add a description"
              onChange={(event) => setDraft(event.target.value)}
              onBlur={commitDescription}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  (event.target as HTMLTextAreaElement).blur();
                } else if (event.key === "Escape") {
                  setDraft(saved);
                }
              }}
              style={{ display: "block", width: "100%", boxSizing: "border-box", margin: 0, padding: 0, border: "none", background: "transparent", resize: "none", overflow: "hidden", outline: "none", fontFamily: "inherit", fontSize: 14, lineHeight: 1.5, color: INK, textWrap: "pretty", fieldSizing: "content" } as React.CSSProperties}
            />
          ) : (
            <div data-testid="record-photo-description" style={{ fontSize: 14, lineHeight: 1.5, color: saved ? INK : TER, textWrap: "pretty" } as React.CSSProperties}>
              {saved || "No description yet"}
            </div>
          )}
          <CopyButton
            label="photo description"
            done={copied === `photo:${photo.id}`}
            hasValue={!!saved}
            size={24}
            style={{ position: "absolute", right: 0, top: -2 }}
            onCopy={() => onCopy(`photo:${photo.id}`, saved)}
          />
        </div>
        {actions.onLocation && (
          <div style={{ marginTop: 4 }}>
            <Property
              key={photo.id}
              compact
              spec={locationSpec}
              state={locationState}
              item={item}
              today={today}
              open={openKey === locationKey}
              onOpenChange={(next) => setOpenKey(next ? locationKey : null)}
              onCommit={() => {}}
              copied={copied}
              onCopy={onCopy}
            />
          </div>
        )}
      </div>

      <div className="rd-noscroll" data-testid="record-photo-strip" style={{ display: "flex", gap: 6, marginTop: 10, overflowX: "auto", padding: 2 }}>
        {evidence.map((entry, index) => {
          const src = entry.thumbnailUrl ?? entry.url;
          const on = index === focus;
          return (
            <button
              key={entry.id}
              type="button"
              className="rd-thumb"
              data-testid="record-photo-thumb"
              aria-pressed={on}
              aria-label={`Photo ${index + 1}${entry.description ? `: ${entry.description}` : ""}`}
              onClick={() => onFocus(index)}
              style={{ flex: "none", position: "relative", width: 64, height: 48, borderRadius: 7, overflow: "hidden", border: 0, padding: 0, background: RD.photoGround, cursor: "pointer", boxShadow: on ? `0 0 0 2px ${INK}` : `0 0 0 1px ${LINE}` }}
            >
              {src && <PhotoImage src={src} alt="" loading="lazy" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} />}
              {!entry.description && (
                <span title="No description" data-testid="record-photo-undescribed" style={{ position: "absolute", right: 5, top: 5, width: 7, height: 7, borderRadius: 4, background: TONE.amber, boxShadow: "0 0 0 2px rgba(255,255,255,.85)" }} />
              )}
            </button>
          );
        })}
        {actions.onAdd && (
          <button
            type="button"
            className="rd-add"
            data-testid="record-photo-add"
            aria-label="Add photo"
            disabled={actions.busy}
            onClick={actions.onAdd}
            style={{ flex: "none", width: 64, height: 48, borderRadius: 7, border: `1.5px dashed ${LINE2}`, background: "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0 }}
          >
            <Plus size={16} color={TER} strokeWidth={2} aria-hidden />
          </button>
        )}
      </div>
      {actions.error && (
        <div role="alert" data-testid="record-photo-error" style={{ marginTop: 8, fontSize: 12.5, color: RED }}>
          {actions.error}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export interface RecordActivityRow {
  id: string;
  sentence: string;
  when: string;
  /** The person, or `undefined` for Led AI / the system. */
  actor?: string;
  /** A sync entry: drawn with the sync state's icon, and `sentence` bold in
   *  its colour. */
  sync?: SyncTone;
  /** After the bold sentence, behind a " · ". Sync entries only. */
  detail?: string;
}

export interface RecordDetailProps {
  item: ConsoleRow;
  evidence: ItemEvidenceRow[];
  activity: RecordActivityRow[];
  siteName: string;
  search: string;
  headerNumber?: string;
  creator?: string;
  /** The title as typed, or the record's. */
  title: string;
  onTitleChange: (title: string) => void;
  /** Report the whole-record patch; the caller debounces. */
  onPatchChange: (patch: Record<string, unknown>) => void;
  /** Write now. */
  onCommit: () => void;
  saveLine: React.ReactNode;
  /** An Autodesk Issue's number, ACC link, sync status and subtype. */
  autodesk?: AutodeskIssueSync;
  /** Re-push a failed sync — the sync card's Retry sync. Absent where the
   *  viewer cannot retry this record. */
  onRetrySync?: RetrySync;
  backHref: string;
  onBack: () => void;
  walkHref?: string;
  onOpenWalk?: () => void;
  onOpenPhoto: (index: number) => void;
  /** Add, remove, describe and locate photos — each only where the caller
   *  can write it. */
  photoActions?: RecordPhotoActions;
  /** Header extras the page already had (Review item, Message). */
  headerExtras?: React.ReactNode;
  /** Fixed instant for due-date arithmetic. Tests pass a literal. */
  now?: Date;
  /** Override the Mac/PC answer. Tests pass a literal. */
  mac?: boolean;
}

export function RecordDetail(props: RecordDetailProps) {
  const { item, evidence, activity, search, onPatchChange, onCommit } = props;
  const kind = recordKind(item);
  const acc = isAutodesk(item);
  const issue = acc && kind !== "task";
  const crumb = issue ? ISSUE_CRUMB : KIND_CRUMB[kind];
  const [accTypes, setAccTypes] = useState<readonly AutodeskSubtypeOption[] | undefined>(undefined);
  const subtypeId = props.autodesk?.subtypeId;
  useEffect(() => {
    if (!issue || !subtypeId) return;
    let live = true;
    void accCatalog(item.jobSiteId, search).then((catalog) => {
      if (live) setAccTypes(catalog);
    });
    return () => {
      live = false;
    };
  }, [issue, subtypeId, item.jobSiteId, search]);
  const state = usePropertyState(item, search, onPatchChange, extendRecordFields);
  const keys = useMemo(() => shortcutKeys(props.mac), [props.mac]);
  const today = useMemo(() => {
    const at = props.now ?? new Date();
    return new Date(at.getFullYear(), at.getMonth(), at.getDate());
  }, [props.now]);
  const { copied, copy } = useCopy();
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [completedHere, setCompletedHere] = useState(false);
  const [photoFocus, setPhotoFocus] = useState(0);
  const focusedPhoto = Math.min(photoFocus, Math.max(0, evidence.length - 1));

  const fieldByKey = new Map(state.fields.map((field) => [field.key, field]));
  const descriptionField = state.fields.find((field) => field.multiline);
  const statusField = fieldByKey.get("status");
  const statusValue = statusField ? state.values.status ?? "" : "";
  const taskDone = kind === "task" && ["closed", "completed"].includes(valueKey(statusValue));

  const toggleDone = () => {
    if (kind !== "task" || !statusField) return;
    const next = taskDone ? "in_progress" : "closed";
    setCompletedHere(!taskDone);
    state.update(statusField, next);
    onCommit();
  };
  // The latest toggle, for the document-level shortcut below.
  const toggleRef = useRef(toggleDone);
  toggleRef.current = toggleDone;

  const sections = recordSections(item)
    .map((section) => ({ ...section, fields: section.keys.map((key) => fieldByKey.get(key)).filter((field): field is ReviewField => !!field && !field.calculated) }))
    .filter((section) => section.fields.length > 0);

  // S, P, A, T… open the property they name — only the ones this record
  // draws, and never while typing or while a popover is already open.
  const hotkeys = new Map<string, string>();
  for (const section of sections) {
    for (const field of section.fields) {
      const hotkey = specFor(field).hotkey;
      if (hotkey) hotkeys.set(hotkey, field.key);
    }
  }
  const hotkeysRef = useRef(hotkeys);
  hotkeysRef.current = hotkeys;
  const openKeyRef = useRef(openKey);
  openKeyRef.current = openKey;
  const photoCountRef = useRef(evidence.length);
  photoCountRef.current = evidence.length;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (kind === "task" && event.key === "Enter" && keys.hasMod(event) && !event.altKey && !event.shiftKey) {
        event.preventDefault();
        toggleRef.current();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey || event.repeat || openKeyRef.current) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || target?.isContentEditable) return;
      // ← and → walk the photos, the design's filmstrip.
      if ((event.key === "ArrowLeft" || event.key === "ArrowRight") && !event.shiftKey) {
        const n = photoCountRef.current;
        if (n > 1) {
          event.preventDefault();
          setPhotoFocus((at) => (Math.min(at, n - 1) + (event.key === "ArrowRight" ? 1 : n - 1)) % n);
        }
        return;
      }
      const key = hotkeysRef.current.get(event.key.toUpperCase());
      if (!key) return;
      event.preventDefault();
      setOpenKey(key);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [kind, keys]);

  // ----- details -----
  const company = state.values.company ?? item.assigneeCompany ?? "";
  const meta: Array<{ key: string; label: string; value: string; icon: React.ReactNode; href?: string; onClick?: () => void }> = [];
  const metaIcon = (Icon: typeof Clock) => <Icon size={15} color={SUB} strokeWidth={2} aria-hidden />;
  // ACC has no company on an Issue, and none of Procore's punch workflow
  // rows below: a row Led AI cannot fill or send reads as a bug.
  if (kind !== "observation" && !issue) meta.push({ key: "company", label: "Company", value: company, icon: metaIcon(Building2) });
  meta.push({ key: "created", label: "Created", value: item.createdAt ? whenStamp(item.createdAt) : "", icon: metaIcon(Clock) });
  meta.push({ key: "creator", label: "Creator", value: props.creator ?? "", icon: metaIcon(User) });
  if (props.walkHref) {
    meta.push({ key: "walk", label: "Walk", value: "Open walk", icon: metaIcon(Footprints), href: props.walkHref, onClick: props.onOpenWalk });
  }
  if (kind === "punch" && !issue) {
    for (const [key, label, Icon] of [
      ["response", "Assignee response", MessageSquare],
      ["manager", "Punch item manager", UserCog],
      ["approver", "Final approver", UserCheck],
      ["notified", "Date notified", Bell],
      ["resolved", "Date resolved", CalendarCheck],
      ["closedAt", "Date closed", Lock],
      ["closedBy", "Closed by", User],
    ] as const) {
      meta.push({ key, label, value: "", icon: metaIcon(Icon) });
    }
  }

  const description = descriptionField ? state.values[descriptionField.key] ?? "" : "";
  const issueType = issue
    ? accIssueTypeLabel(item, { subtypeId, catalog: accTypes, observationType: fieldByKey.get("type")?.rawValue })
    : "";
  const syncLook = recordSyncLook(item, props.autodesk);
  const issueNumber = props.autodesk?.number;
  // ACC's Type › Subtype, read-only, where the Procore page draws its own
  // Classification: between Properties and Location.
  const accClassification = issue ? (
              <section aria-label="Classification">
                <div style={{ fontSize: 13, fontWeight: 500, color: SUB, paddingBottom: 6 }}>Classification</div>
                <div className="rd-row" data-testid="record-acc-type" style={{ display: "flex", alignItems: "center", gap: 9, height: 32, margin: "0 -8px", padding: "0 8px", fontSize: 13.5, color: INK }}>
                  <span style={{ width: 16, display: "inline-flex", justifyContent: "center", flex: "none" }}>
                    <Tag size={15} color={SUB} strokeWidth={2} aria-hidden />
                  </span>
                  <span style={{ minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={issueType}>
                    <span style={{ color: SUB }}>Type </span>
                    {issueType}
                  </span>
                </div>
              </section>
  ) : null;

  return (
    <div
      data-testid="record-detail"
      data-kind={kind}
      style={{ ...PHONE_TOKENS, flex: 1, minHeight: 0, overflow: "auto", background: BG, color: INK, fontFamily: "Inter, -apple-system, system-ui, sans-serif" }}
      // Leaving the title or description commits it — see `ItemDetail`'s
      // debounce. React's onBlur is focusout, which bubbles.
      onBlur={(event) => {
        const target = event.target as HTMLElement;
        if (target.dataset.commitOnBlur) onCommit();
      }}
    >
      <style>{RECORD_CSS}</style>
      <div style={{ maxWidth: 1160, margin: "0 auto", padding: "36px 40px 220px", boxSizing: "border-box" }}>
        {/* ---------- crumb + actions ---------- */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <nav aria-label="Breadcrumb" style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, fontFamily: MONO, fontSize: 10, fontWeight: 600, letterSpacing: 1.3, color: SUB, textTransform: "uppercase" }}>
            <span style={{ minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{props.siteName}</span>
            <span style={{ color: RD.crumbRule }}>›</span>
            <a
              href={props.backHref}
              data-testid="detail-back"
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey) return;
                event.preventDefault();
                props.onBack();
              }}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, color: SUB, whiteSpace: "nowrap" }}
            >
              {crumb.icon}
              {crumb.plural}
            </a>
            {props.headerNumber && (
              <>
                <span style={{ color: RD.crumbRule }}>›</span>
                <span data-testid="detail-remote-id" style={{ color: INK, whiteSpace: "nowrap" }}>{`#${props.headerNumber.replace(/^#/, "")}`}</span>
              </>
            )}
          </nav>
          <div style={{ flex: "none", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {props.saveLine}
            {props.headerExtras}
            {kind === "task" && statusField && (
              <button
                type="button"
                className="rd-done"
                data-testid="record-mark-complete"
                aria-pressed={taskDone}
                aria-keyshortcuts={keys.mod === "⌘" ? "Meta+Enter" : "Control+Enter"}
                onClick={toggleDone}
                title={`${taskDone ? "Mark incomplete" : "Mark complete"} — press ${keys.mod} + ${keys.enter}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  height: 32,
                  padding: "0 12px 0 10px",
                  borderRadius: 8,
                  border: `1px solid ${taskDone ? INK : LINE2}`,
                  background: taskDone ? INK : CARD,
                  color: taskDone ? CARD : INK,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  fontFamily: "inherit",
                  transition: "background .15s, border-color .15s, color .15s",
                }}
              >
                <span style={{ width: 16, height: 16, borderRadius: "50%", border: `1.5px solid ${taskDone ? GREEN : SUB}`, background: taskDone ? GREEN : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box" }}>
                  {taskDone && <Check size={10} color={INK} strokeWidth={3} aria-hidden />}
                </span>
                <span>{taskDone ? "Completed" : "Mark complete"}</span>
                <span data-testid="record-shortcut" style={{ display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 4 }}>
                  {[keys.mod, keys.enter].map((cap) => (
                    <kbd
                      key={cap}
                      style={{
                        fontFamily: MONO,
                        fontSize: 10,
                        fontWeight: 600,
                        lineHeight: 1,
                        color: taskDone ? "rgba(255,253,248,.75)" : SUB,
                        background: taskDone ? "rgba(255,253,248,.10)" : BG,
                        border: `1px solid ${taskDone ? "rgba(255,253,248,.28)" : LINE2}`,
                        borderBottomWidth: 2,
                        borderRadius: 5,
                        padding: "3px 5px",
                      }}
                    >
                      {cap}
                    </kbd>
                  ))}
                </span>
              </button>
            )}
            <button
              type="button"
              className="rd-btn"
              data-testid="copy-link"
              onClick={async () => {
                if (await copyToClipboard(location.href)) {
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 1400);
                }
              }}
              style={{ flex: "none", display: "inline-flex", alignItems: "center", gap: 7, height: 32, padding: "0 10px", borderRadius: 8, background: "transparent", border: `1px solid ${LINE}`, fontSize: 13, fontWeight: 600, color: SUB, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit" }}
            >
              {linkCopied ? <Check size={13} color={RD.copied} strokeWidth={2.4} aria-hidden /> : <LinkIcon size={13} color={SUB} strokeWidth={2} aria-hidden />}
              {linkCopied ? "Copied" : "Copy link"}
            </button>
          </div>
        </div>

        <div className="rd-grid" style={{ marginTop: 40 }}>
          {/* ===== main ===== */}
          <div style={{ minWidth: 0, maxWidth: 720 }}>
            {issue && (
              <div data-testid="record-eyebrow" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontFamily: MONO, fontSize: 10.5, fontWeight: 600, letterSpacing: 1.3, color: SUB, textTransform: "uppercase" }}>
                <span style={{ color: TONE.amber }}>Issue</span>
                {item.createdAt && (
                  <>
                    <span style={{ color: RD.crumbRule }}>·</span>
                    <span>{whenStamp(item.createdAt)}</span>
                  </>
                )}
              </div>
            )}
            <textarea
              className="rd-title"
              data-testid="detail-title"
              data-commit-on-blur="1"
              aria-label="Title"
              rows={1}
              value={props.title}
              placeholder="Untitled"
              onChange={(event) => props.onTitleChange(event.target.value)}
              style={{
                display: "block",
                width: "100%",
                boxSizing: "border-box",
                margin: 0,
                padding: 0,
                border: "none",
                background: "transparent",
                resize: "none",
                overflow: "hidden",
                outline: "none",
                fontFamily: "inherit",
                fontSize: 28,
                fontWeight: 700,
                letterSpacing: -0.7,
                lineHeight: 1.25,
                textWrap: "balance",
                color: taskDone ? SUB : INK,
                textDecoration: taskDone ? "line-through" : "none",
                textDecorationColor: "rgba(20,17,12,.35)",
                textDecorationThickness: 1.5,
                fieldSizing: "content",
              } as React.CSSProperties}
            />
            {taskDone && (
              <div
                data-testid="record-complete-banner"
                style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 13, color: SUB, animation: "rdFadeIn .2s both" }}
              >
                <Check size={13} color={RD.copied} strokeWidth={2.4} aria-hidden />
                <span>{completedHere ? "Completed just now" : "Completed"}</span>
                <span style={{ color: RD.crumbRule }}>·</span>
                <button
                  type="button"
                  className="rd-link"
                  onClick={toggleDone}
                  style={{ border: 0, background: "transparent", padding: 0, color: INK, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                >
                  Mark incomplete
                </button>
              </div>
            )}

            {descriptionField && (
              <div className="rd-row" style={{ position: "relative", margin: "22px -8px 0", padding: "0 30px 0 8px", borderRadius: 8 }}>
                <textarea
                  className="rd-desc"
                  data-testid="record-description"
                  data-commit-on-blur="1"
                  aria-label={descriptionField.label}
                  rows={2}
                  value={description}
                  placeholder="Add a description…"
                  onChange={(event) => state.update(descriptionField, event.target.value)}
                  style={{
                    display: "block",
                    width: "100%",
                    boxSizing: "border-box",
                    margin: 0,
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    resize: "none",
                    overflow: "hidden",
                    outline: "none",
                    fontFamily: "inherit",
                    fontSize: 16,
                    lineHeight: 1.65,
                    color: INK,
                    textWrap: "pretty",
                    fieldSizing: "content",
                  } as React.CSSProperties}
                />
                <CopyButton
                  label="description"
                  done={copied === "description"}
                  hasValue={!!description.trim()}
                  size={26}
                  style={{ position: "absolute", right: 2, top: 2, borderRadius: 7 }}
                  onCopy={() => void copy("description", description)}
                />
              </div>
            )}

            {/* ---------- photos ---------- */}
            <div style={{ marginTop: 30 }}>
              <PhotoCarousel
                evidence={evidence}
                focus={focusedPhoto}
                onFocus={setPhotoFocus}
                noun={crumb.noun}
                actions={props.photoActions ?? {}}
                state={state}
                item={item}
                today={today}
                openKey={openKey}
                setOpenKey={setOpenKey}
                copied={copied}
                onCopy={(key, text) => void copy(key, text)}
                onOpenPhoto={props.onOpenPhoto}
              />
            </div>

            {/* ---------- activity ---------- */}
            <div style={{ marginTop: 44 }} data-testid="history-card">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 10, borderBottom: `1px solid ${LINE}` }}>
                <span style={{ fontSize: 15, fontWeight: 600 }}>Activity</span>
                <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 600, letterSpacing: 1.3, color: QUIET }}>NEWEST FIRST</span>
              </div>
              {activity.length === 0 ? (
                <div data-testid="history-empty" style={{ padding: "14px 0", fontSize: 13.5, color: SUB }}>
                  Nothing has happened to this {crumb.noun} yet.
                </div>
              ) : (
                activity.map((entry) => (
                  <div key={entry.id} data-testid="history-entry" style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 0", borderBottom: `1px solid ${HAIR}` }}>
                    {entry.sync ? (
                      <span data-sync={entry.sync} style={{ flex: "none", width: 22, height: 22, borderRadius: 11, background: SYNC_TONE[entry.sync].bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {(() => {
                          const tone = SYNC_TONE[entry.sync];
                          return <tone.Icon size={12} color={tone.fg} strokeWidth={2.4} aria-hidden />;
                        })()}
                      </span>
                    ) : entry.actor ? (
                      <span style={{ flex: "none", width: 22, height: 22, borderRadius: 11, background: INK, color: RD.white, fontSize: 8, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {initialsOf(entry.actor)}
                      </span>
                    ) : (
                      <span style={{ flex: "none", width: 22, height: 22, borderRadius: 11, background: SEC, color: RD.ledMark, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Sparkles size={12} strokeWidth={2} aria-hidden />
                      </span>
                    )}
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: SUB }}>
                      {entry.sync ? (
                        <>
                          <strong style={{ fontWeight: 600, color: SYNC_TONE[entry.sync].fg }}>{entry.sentence}</strong>
                          {entry.detail && ` · ${entry.detail}`}
                        </>
                      ) : (
                        entry.sentence
                      )}
                    </span>
                    <span style={{ flex: "none", fontSize: 12, color: QUIET, whiteSpace: "nowrap" }}>{entry.when}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ===== sidebar ===== */}
          <aside className="rd-side" data-testid="record-sidebar" style={{ position: "sticky", top: 24, display: "flex", flexDirection: "column", gap: 22 }}>
            {sections.map((section) => (
              <Fragment key={section.title}>
              {section.title === "Location" && accClassification}
              <section aria-label={section.title}>
                <div style={{ fontSize: 13, fontWeight: 500, color: SUB, paddingBottom: 6 }}>{section.title}</div>
                {section.fields.map((field) => (
                  <Property
                    key={field.key}
                    spec={specFor(field)}
                    state={state}
                    item={item}
                    today={today}
                    open={openKey === field.key}
                    onOpenChange={(next) => setOpenKey(next ? field.key : null)}
                    onCommit={onCommit}
                    copied={copied}
                    onCopy={(key, text) => void copy(key, text)}
                  />
                ))}
              </section>
              </Fragment>
            ))}
            {!sections.some((section) => section.title === "Location") && accClassification}
            {syncLook && <SyncRow look={syncLook} onRetry={props.onRetrySync} />}
            {acc && !syncLook && issueNumber ? (
              // An Issue with a number and no sync state on file: the link alone.
              <section aria-label="Autodesk" data-testid="record-autodesk">
                <div style={{ display: "flex", alignItems: "center", gap: 9, height: 32, fontSize: 13.5 }}>
                  <AutodeskGlyph size={15} />
                  {props.autodesk?.url ? (
                    <a data-testid="record-acc-link" href={props.autodesk.url} target="_blank" rel="noreferrer" style={{ color: INK, fontWeight: 600, textDecoration: "underline", textDecorationColor: "rgba(20,17,12,.2)", textUnderlineOffset: 3 }}>
                      {`Issue #${issueNumber}`}
                    </a>
                  ) : (
                    <span data-testid="record-acc-link" style={{ color: INK, fontWeight: 600 }}>{`Issue #${issueNumber}`}</span>
                  )}
                </div>
              </section>
            ) : null}
            <section aria-label="Details">
              <div style={{ fontSize: 13, fontWeight: 500, color: SUB, paddingBottom: 6 }}>Details</div>
              {meta.map((entry) => (
                <div key={entry.key} className="rd-row" data-testid="record-meta" data-meta={entry.key} style={{ display: "flex", alignItems: "center", gap: 4, margin: "0 -8px" }}>
                  <span style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 9, height: 32, padding: "0 8px", fontSize: 13.5, color: INK }}>
                    <span style={{ width: 16, display: "inline-flex", justifyContent: "center", flex: "none" }}>{entry.icon}</span>
                    <span style={{ minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      <span style={{ color: SUB }}>{`${entry.label} `}</span>
                      {entry.href ? (
                        <a
                          href={entry.href}
                          onClick={(event) => {
                            if (!entry.onClick || event.metaKey || event.ctrlKey || event.shiftKey) return;
                            event.preventDefault();
                            entry.onClick();
                          }}
                          style={{ color: INK, fontWeight: 600, textDecoration: "underline", textDecorationColor: "rgba(20,17,12,.2)", textUnderlineOffset: 3 }}
                        >
                          {entry.value}
                        </a>
                      ) : (
                        <span style={{ color: entry.value ? INK : TER }}>{entry.value || "—"}</span>
                      )}
                    </span>
                  </span>
                  {!entry.href && (
                    <CopyButton label={entry.label.toLowerCase()} done={copied === `meta:${entry.key}`} hasValue={!!entry.value} onCopy={() => void copy(`meta:${entry.key}`, entry.value)} />
                  )}
                </div>
              ))}
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}
